import { GCD, MELEE_RANGE } from './constants';

export type School =
  'physical' | 'holy' | 'fire' | 'frost' | 'arcane' | 'shadow' | 'nature';
export type ResourceType = 'mana' | 'rage' | 'energy';
/** `frozen` is Ice Block: like a stun, but self-inflicted and never diminished. */
export type CcType =
  'stun' | 'incapacitate' | 'fear' | 'root' | 'silence' | 'frozen';
export type DrCategory = 'stun' | 'incapacitate' | 'fear' | 'root' | 'silence';
/** Holds for the unit it's checked on. */
export interface Condition {
  /** Below this fraction of its health. */
  below?: number;
  /** Not moving or in the air. */
  still?: true;
  outOfCombat?: true;
}

export type ClassId =
  | 'warrior'
  | 'mage'
  | 'priest'
  | 'rogue'
  | 'hunter'
  | 'paladin'
  | 'druid'
  | 'shaman';

export interface AuraDef {
  name: string;
  /** Icon id in `assets/icons/`; defaults to the aura's own id. */
  icon?: string;
  description: string;
  harmful: boolean;
  /** Seconds; 0 lasts until removed. */
  duration: number;
  dispel?: 'magic';
  cc?: CcType;
  dr?: DrCategory;
  /** Breaks once the bearer has taken more than this much damage since it was applied. */
  breakOnDamage?: number;
  periodic?: {
    interval: number;
    damage?: number;
    heal?: number;
    school: School;
  };
  absorb?: number;
  /** Added to the run-speed multiplier: -0.5 is a 50% snare. The strongest snare wins. */
  moveSpeed?: number;
  damageTaken?: number;
  healingTaken?: number;
  /** Ignores damage, harmful auras and interrupts: from every school, or only physical or only magic ones. */
  immune?: true | 'physical' | 'magic';
  stealth?: boolean;
  /** Damage doesn't break the bearer's stealth. */
  shieldsStealth?: boolean;
  /** Roots and snares have no effect on the bearer. */
  freedom?: boolean;
  /** Forbearance-style marker: ignores immunity, starts no combat, and nothing removes it early. */
  exhaustion?: boolean;
  /** A shapeshift: any ability on the GCD drops it, so only one is ever up. */
  form?: boolean;
  /** The next enemy spell aimed at the bearer lands on its caster instead, and this fades. */
  reflect?: boolean;
  /** Fraction of physical damage ignored; only the bearer's strongest counts. */
  armor?: number;
  /** Fraction of magic damage ignored; only the bearer's strongest counts. */
  resistance?: number;
  /** A class's innate or a discipline: there from the start, never removed, and never sent in snapshots. */
  passive?: boolean;
  /** Fraction more damage, or healing, the bearer deals. */
  damageDone?: number;
  healingDone?: number;
  /** Fraction of the damage the bearer deals that heals them. */
  lifesteal?: number;
  /** Auto-attacks come this fraction faster. */
  swingSpeed?: number;
  // Ability and aura ids below are plain strings: naming their types here would make them circular. `sim.test.ts` checks each exists.
  /** Spent on the bearer's next use of one of `abilities`: without its cast time, or without stealth. */
  next?: { abilities: string[]; instant?: true; stealthed?: true };
  /** These abilities' cooldowns are `by` (a fraction) shorter. */
  haste?: { abilities: string[]; by: number };
  /** Its numbers count only while this holds for the bearer; periodic ones tick only then. */
  when?: Condition;
  /** Damage it adds counts only against rooted targets. */
  againstRooted?: true;
  /** A discipline's numbers count only for its own class's abilities, and auto-attacks while playing it. */
  own?: true;
  /** Given to the bearer whenever they leave stealth. */
  afterStealth?: string;
  /** A killing blow leaves the bearer at this fraction of health instead, once: this aura goes. */
  cheatDeath?: number;
  /** The first hit the bearer takes every `every` seconds is scaled by `factor`. */
  ward?: { every: number; factor: number };
  /** Each combo point a finisher spends has this chance to come straight back. */
  comboBack?: number;
  /** Roots and snares on the bearer last this fraction shorter. */
  shed?: number;
  /** Swapping class doesn't end the bearer's shapeshift. */
  keepsForm?: true;
  /** The bearer's totems have this fraction more health. */
  totemHealth?: number;
  /** Damage never pushes back the bearer's casts. */
  noPushback?: true;
  /** Abilities reach this many yards farther. */
  reach?: number;
  /** Abilities cost this fraction less. */
  thrift?: number;
}

export type Effect =
  | { type: 'damage'; amount: number; perCombo?: number }
  | { type: 'heal'; amount: number }
  | { type: 'aura'; aura: AuraId; self?: boolean; perCombo?: number }
  | { type: 'interrupt'; lockout: number }
  /** Strips harmful magic from a friend, helpful magic from an enemy. */
  | { type: 'dispel'; count: number }
  | { type: 'charge'; speed: number }
  | { type: 'blink'; distance: number }
  /** Jumps an arc `height` tall that lands `distance` away on flat ground; negative goes backward. */
  | { type: 'leap'; distance: number; height: number }
  | { type: 'breakCc'; cc: CcType[] }
  | { type: 'removeSnares' }
  | { type: 'cleanse' }
  /** Pulls the target out of stealth. */
  | { type: 'reveal' }
  | { type: 'resource'; amount: number }
  /** Restores the target's mana; does nothing for rage or energy. */
  | { type: 'mana'; amount: number }
  /** Applies `effects` to every enemy (or ally, if `friendly`) within `radius` of the caster, or of the spot a ground ability lands on. */
  | { type: 'aoe'; radius: number; effects: Effect[]; friendly?: boolean }
  /**
   * Applies `effects` to the target, then leaps up to `jumps` times, each from the last unit hit to
   * another on the target's side within `radius` and in its sight: the nearest, or the most hurt
   * when healing. No unit is hit twice, and each leap's damage and healing is `falloff` of the last.
   */
  | {
      type: 'chain';
      jumps: number;
      radius: number;
      falloff: number;
      effects: Effect[];
    }
  /** Leaves a zone where a ground ability lands, or at the caster's feet. */
  | { type: 'zone'; zone: ZoneId }
  /** Makes the caster's other class the one they play: its spells, resource, GCD and auto-attack. */
  | { type: 'swap' }
  /** The caster's ability with this id is ready at once. */
  | { type: 'cooldown'; ability: string }
  /** Enemies attacking or casting at the target stop, as if it had stealthed. */
  | { type: 'shake' };

/** An ability's icon is `assets/icons/<ability id>.webp`. */
export interface AbilityDef {
  name: string;
  /** Icon id in `assets/icons/`; defaults to the ability's own id. */
  icon?: string;
  /** Talents only: fires only while this holds for the caster. */
  when?: Condition;
  description: string;
  school: School;
  /** `ground` aims at a spot the caster can see within `range`. */
  target: 'enemy' | 'friendly' | 'self' | 'any' | 'ground';
  cost?: number;
  castTime?: number;
  channel?: { duration: number; ticks: number };
  cooldown?: number;
  /** Times it can be used per match. */
  uses?: number;
  /** Global cooldown this triggers; defaults to the class GCD, 0 for off-GCD abilities. */
  gcd?: number;
  /** Edge-to-edge distance; enemy abilities without one are melee. */
  range?: number;
  minRange?: number;
  usableWhileCc?: boolean;
  requiresStealth?: boolean;
  outOfCombatOnly?: boolean;
  /** Using this doesn't break the caster's stealth. */
  keepsStealth?: boolean;
  combo?: number;
  finisher?: boolean;
  /** Only usable on a target below this health fraction. */
  executeBelow?: number;
  /** Recasting while the self aura it applies is up cancels that aura instead; stealth only clicks off, so mashing can't drop it. */
  cancelable?: boolean;
  /** Applied to the target on use; the ability can't be used on a target that has it. */
  exhaustion?: AuraId;
  /** Taking damage cancels the cast or channel. */
  interruptedByDamage?: boolean;
  projectileSpeed?: number;
  effects: Effect[];
}

export interface ClassDef {
  name: string;
  color: string;
  description: string;
  /** Always on while this is one of the player's two classes; two classes sharing one get it once. */
  innate: AuraId;
  /** Three options per slot. Arrivals and departures are abilities Swap Class fires; disciplines are passive auras. */
  talents: {
    arrival: AbilityId[];
    departure: AbilityId[];
    discipline: AuraId[];
  };
  resource: ResourceType;
  maxResource: number;
  startResource: number;
  /** Resource per second; mana pauses for five seconds after spending any. */
  regen: number;
  gcd: number;
  /**
   * Auto-attack. `range` makes it a ranged shot that needs line of sight and a target at least
   * `minRange` away; with `aim`, the last that many seconds before each shot must be spent standing still.
   */
  swing: {
    interval: number;
    damage: number;
    rage?: number;
    range?: number;
    minRange?: number;
    aim?: number;
  };
  /** Its hostile spells leave auto-attack as it was; only a right-click starts it. */
  caster?: boolean;
  abilities: AbilityId[];
}

export type ZoneId =
  | 'jaw-trap'
  | 'bloom'
  | 'healing-stream-totem'
  | 'earthbind-totem'
  | 'tremor-totem'
  | 'grounding-totem'
  | 'war-banner'
  | 'rune-of-detonation'
  | 'spirit-lantern'
  | 'caltrops'
  | 'freezing-trap'
  | 'flare'
  | 'hallowed-ground'
  | 'hallowed-ground-heal'
  | 'thornfield'
  | 'consecration'
  | 'consecration-heal';

/** Something left on the ground, owned by its caster and gone when they die. */
export interface ZoneDef {
  radius: number;
  duration: number;
  /** Seconds before it can go off. */
  armDelay?: number;
  /**
   * Seconds between applying `effects` to everyone inside. Without it the zone is a trap: it goes
   * off once, on the first unit to step in, and pulls that unit out of stealth.
   * Pulsing an aura with `dr` would diminish it on every pulse.
   */
  pulse?: number;
  /** Affects allies instead of enemies. */
  friendly?: boolean;
  /** Enemies never see it. */
  hidden?: boolean;
  /** Per caster: placing another removes the oldest. */
  max?: number;
  /** Never pulses or springs: the first enemy spell to land on an ally inside is lost, and the zone with it. */
  grounding?: boolean;
  /** Stands a totem with this much health in the middle; killing it ends the zone. */
  totem?: number;
  effects: Effect[];
}

export const AURAS = {
  'mortal-wound': {
    name: 'Mortal Wound',
    icon: 'mortal-strike',
    description: 'Healing received reduced by 35%.',
    harmful: true,
    duration: 8,
    healingTaken: -0.35,
  },
  hamstring: {
    name: 'Hamstring',
    description: 'Movement slowed by 50%.',
    harmful: true,
    duration: 12,
    moveSpeed: -0.5,
  },
  rend: {
    name: 'Rend',
    description: 'Bleeding over time.',
    harmful: true,
    duration: 15,
    periodic: { interval: 3, damage: 90, school: 'physical' },
  },
  'charge-root': {
    name: 'Charge',
    icon: 'charge',
    description: 'Rooted.',
    harmful: true,
    duration: 1.5,
    cc: 'root',
    dr: 'root',
  },
  'intimidating-shout': {
    name: 'Intimidating Shout',
    description: 'Fleeing in terror.',
    harmful: true,
    duration: 6,
    cc: 'fear',
    dr: 'fear',
    breakOnDamage: 600,
  },
  'shield-wall': {
    name: 'Shield Wall',
    description: 'Damage taken reduced by 40%.',
    harmful: false,
    duration: 8,
    damageTaken: -0.4,
  },
  chilled: {
    name: 'Chilled',
    description: 'Movement slowed by 40%.',
    harmful: true,
    duration: 8,
    dispel: 'magic',
    moveSpeed: -0.4,
  },
  fireball: {
    name: 'Fireball',
    description: 'Fire damage over time.',
    harmful: true,
    duration: 8,
    dispel: 'magic',
    periodic: { interval: 2, damage: 40, school: 'fire' },
  },
  polymorph: {
    name: 'Polymorph',
    description: 'Incapacitated. Any damage breaks it.',
    harmful: true,
    duration: 8,
    dispel: 'magic',
    cc: 'incapacitate',
    dr: 'incapacitate',
    breakOnDamage: 0,
  },
  'frost-nova': {
    name: 'Frost Nova',
    description: 'Frozen in place.',
    harmful: true,
    duration: 6,
    dispel: 'magic',
    cc: 'root',
    dr: 'root',
    breakOnDamage: 400,
  },
  'ice-block': {
    name: 'Ice Block',
    description: 'Immune to all damage and harmful effects, but cannot act.',
    harmful: false,
    duration: 10,
    cc: 'frozen',
    immune: true,
  },
  'ice-barrier': {
    name: 'Ice Barrier',
    description: 'Absorbs damage.',
    harmful: false,
    duration: 30,
    dispel: 'magic',
    absorb: 900,
  },
  renew: {
    name: 'Renew',
    description: 'Healing over time.',
    harmful: false,
    duration: 15,
    dispel: 'magic',
    periodic: { interval: 3, heal: 160, school: 'holy' },
  },
  'power-word-shield': {
    name: 'Power Word: Shield',
    description: 'Absorbs damage.',
    harmful: false,
    duration: 15,
    dispel: 'magic',
    absorb: 800,
  },
  'pain-suppression': {
    name: 'Pain Suppression',
    description: 'Damage taken reduced by 40%.',
    harmful: false,
    duration: 8,
    dispel: 'magic',
    damageTaken: -0.4,
  },
  'shadow-word-pain': {
    name: 'Shadow Word: Pain',
    description: 'Shadow damage over time.',
    harmful: true,
    duration: 18,
    dispel: 'magic',
    periodic: { interval: 3, damage: 110, school: 'shadow' },
  },
  'mind-flay': {
    name: 'Mind Flay',
    description: 'Movement slowed by 50%.',
    harmful: true,
    duration: 1.5,
    moveSpeed: -0.5,
  },
  'psychic-scream': {
    name: 'Psychic Scream',
    description: 'Fleeing in terror.',
    harmful: true,
    duration: 6,
    dispel: 'magic',
    cc: 'fear',
    dr: 'fear',
    breakOnDamage: 600,
  },
  stealth: {
    name: 'Stealth',
    description: 'Invisible to enemies. Acting or taking damage breaks it.',
    harmful: false,
    duration: 0,
    stealth: true,
    breakOnDamage: 0,
  },
  vanish: {
    name: 'Vanish',
    description: 'Damage does not break stealth.',
    harmful: false,
    duration: 2,
    shieldsStealth: true,
  },
  'cheap-shot': {
    name: 'Cheap Shot',
    description: 'Stunned.',
    harmful: true,
    duration: 4,
    cc: 'stun',
    dr: 'stun',
  },
  'kidney-shot': {
    name: 'Kidney Shot',
    description: 'Stunned.',
    harmful: true,
    duration: 1,
    cc: 'stun',
    dr: 'stun',
  },
  gouge: {
    name: 'Gouge',
    description: 'Incapacitated. Any damage breaks it.',
    harmful: true,
    duration: 4,
    cc: 'incapacitate',
    dr: 'incapacitate',
    breakOnDamage: 0,
  },
  sprint: {
    name: 'Sprint',
    description: 'Movement speed increased by 70%.',
    harmful: false,
    duration: 8,
    moveSpeed: 0.7,
  },
  'aimed-shot': {
    name: 'Aimed Shot',
    description: 'Healing received reduced by 35%.',
    harmful: true,
    duration: 10,
    healingTaken: -0.35,
  },
  'serpent-sting': {
    name: 'Serpent Sting',
    description: 'Nature damage over time.',
    harmful: true,
    duration: 15,
    periodic: { interval: 3, damage: 100, school: 'nature' },
  },
  'concussive-shot': {
    name: 'Concussive Shot',
    description: 'Movement slowed by 50%.',
    harmful: true,
    duration: 4,
    moveSpeed: -0.5,
  },
  'scatter-shot': {
    name: 'Scatter Shot',
    description: 'Disoriented. Any damage breaks it.',
    harmful: true,
    duration: 4,
    cc: 'incapacitate',
    dr: 'incapacitate',
    breakOnDamage: 0,
  },
  'silencing-shot': {
    name: 'Silencing Shot',
    description: 'Silenced.',
    harmful: true,
    duration: 3,
    cc: 'silence',
    dr: 'silence',
  },
  'hammer-of-justice': {
    name: 'Hammer of Justice',
    description: 'Stunned.',
    harmful: true,
    duration: 5,
    dispel: 'magic',
    cc: 'stun',
    dr: 'stun',
  },
  'divine-shield': {
    name: 'Divine Shield',
    description: 'Immune to all damage and harmful effects.',
    harmful: false,
    duration: 8,
    immune: true,
  },
  'blessing-of-freedom': {
    name: 'Blessing of Freedom',
    description: 'Immune to movement impairing effects.',
    harmful: false,
    duration: 6,
    dispel: 'magic',
    freedom: true,
  },
  rejuvenation: {
    name: 'Rejuvenation',
    description: 'Healing over time.',
    harmful: false,
    duration: 12,
    dispel: 'magic',
    periodic: { interval: 3, heal: 200, school: 'nature' },
  },
  regrowth: {
    name: 'Regrowth',
    description: 'Healing over time.',
    harmful: false,
    duration: 6,
    dispel: 'magic',
    periodic: { interval: 2, heal: 100, school: 'nature' },
  },
  moonfire: {
    name: 'Moonfire',
    description: 'Arcane damage over time.',
    harmful: true,
    duration: 12,
    dispel: 'magic',
    periodic: { interval: 3, damage: 75, school: 'arcane' },
  },
  'entangling-roots': {
    name: 'Entangling Roots',
    description: 'Rooted.',
    harmful: true,
    duration: 8,
    dispel: 'magic',
    cc: 'root',
    dr: 'root',
    breakOnDamage: 500,
  },
  cyclone: {
    name: 'Cyclone',
    description:
      'Tossed in a cyclone: cannot act, and immune to damage, healing and harmful effects.',
    harmful: true,
    duration: 6,
    cc: 'incapacitate',
    dr: 'incapacitate',
    immune: true,
    healingTaken: -1,
  },
  barkskin: {
    name: 'Barkskin',
    description: 'Damage taken reduced by 20%.',
    harmful: false,
    duration: 12,
    damageTaken: -0.2,
  },
  'bear-form': {
    name: 'Bear Form',
    description: 'Damage taken reduced by 25%.',
    harmful: false,
    duration: 0,
    damageTaken: -0.25,
    form: true,
  },
  'travel-form': {
    name: 'Travel Form',
    description: 'Movement speed increased by 40%.',
    harmful: false,
    duration: 0,
    moveSpeed: 0.4,
    form: true,
  },
  'recently-bandaged': {
    name: 'Recently Bandaged',
    icon: 'bandage',
    description: 'Cannot be bandaged.',
    harmful: true,
    duration: 60,
    exhaustion: true,
  },
  'regeneration-potion': {
    name: 'Regeneration Potion',
    description: 'Healing over time.',
    harmful: false,
    duration: 15,
    periodic: { interval: 3, heal: 400, school: 'nature' },
  },
  'spell-reflection': {
    name: 'Spell Reflection',
    description: 'Reflecting the next spell cast on you.',
    harmful: false,
    duration: 5,
    reflect: true,
  },
  'jaw-trap': {
    name: 'Jaw Trap',
    description: 'Rooted.',
    harmful: true,
    duration: 4,
    cc: 'root',
    dr: 'root',
  },
  'flame-shock': {
    name: 'Flame Shock',
    description: 'Fire damage over time.',
    harmful: true,
    duration: 12,
    dispel: 'magic',
    periodic: { interval: 2, damage: 50, school: 'fire' },
  },
  'frost-shock': {
    name: 'Frost Shock',
    description: 'Movement slowed by 50%.',
    harmful: true,
    duration: 8,
    dispel: 'magic',
    moveSpeed: -0.5,
  },
  earthbind: {
    name: 'Earthbind',
    icon: 'earthbind-totem',
    description: 'Movement slowed by 50%.',
    harmful: true,
    duration: 1.5,
    moveSpeed: -0.5,
  },
  riptide: {
    name: 'Riptide',
    description: 'Healing over time.',
    harmful: false,
    duration: 12,
    dispel: 'magic',
    periodic: { interval: 3, heal: 120, school: 'nature' },
  },
  hex: {
    name: 'Hex',
    description: 'Incapacitated. Enough damage breaks it.',
    harmful: true,
    duration: 6,
    dispel: 'magic',
    cc: 'incapacitate',
    dr: 'incapacitate',
    breakOnDamage: 300,
  },
  'piercing-howl': {
    name: 'Piercing Howl',
    description: 'Movement slowed by 50%.',
    harmful: true,
    duration: 6,
    moveSpeed: -0.5,
  },
  rallied: {
    name: 'War Banner',
    icon: 'war-banner',
    description:
      'Damage taken reduced by 15%. Immune to movement impairing effects.',
    harmful: false,
    duration: 1.5,
    damageTaken: -0.15,
    freedom: true,
  },
  scorched: {
    name: 'Scorched',
    icon: 'rune-of-detonation',
    description: 'Movement slowed by 40%.',
    harmful: true,
    duration: 4,
    dispel: 'magic',
    moveSpeed: -0.4,
  },
  silence: {
    name: 'Silence',
    description: 'Silenced.',
    harmful: true,
    duration: 4,
    dispel: 'magic',
    cc: 'silence',
    dr: 'silence',
  },
  blind: {
    name: 'Blind',
    description: 'Incapacitated. Any damage breaks it.',
    harmful: true,
    duration: 8,
    cc: 'incapacitate',
    dr: 'incapacitate',
    breakOnDamage: 0,
  },
  'cloak-of-shadows': {
    name: 'Cloak of Shadows',
    description: 'Immune to spells.',
    harmful: false,
    duration: 5,
    immune: 'magic',
  },
  crippled: {
    name: 'Caltrops',
    icon: 'caltrops',
    description: 'Movement slowed by 50%.',
    harmful: true,
    duration: 1.5,
    moveSpeed: -0.5,
  },
  'freezing-trap': {
    name: 'Freezing Trap',
    description: 'Frozen solid. Any damage breaks it.',
    harmful: true,
    duration: 8,
    dispel: 'magic',
    cc: 'incapacitate',
    dr: 'incapacitate',
    breakOnDamage: 0,
  },
  repentance: {
    name: 'Repentance',
    description: 'Incapacitated. Any damage breaks it.',
    harmful: true,
    duration: 6,
    dispel: 'magic',
    cc: 'incapacitate',
    dr: 'incapacitate',
    breakOnDamage: 0,
  },
  'blessing-of-protection': {
    name: 'Blessing of Protection',
    description: 'Immune to physical damage and effects.',
    harmful: false,
    duration: 8,
    dispel: 'magic',
    immune: 'physical',
  },
  forbearance: {
    name: 'Forbearance',
    icon: 'blessing-of-protection',
    description: 'Cannot receive Blessing of Protection.',
    harmful: true,
    duration: 30,
    exhaustion: true,
  },
  'mighty-bash': {
    name: 'Mighty Bash',
    description: 'Stunned.',
    harmful: true,
    duration: 4,
    cc: 'stun',
    dr: 'stun',
  },
  brambles: {
    name: 'Thornfield',
    icon: 'thornfield',
    description: 'Movement slowed by 30%.',
    harmful: true,
    duration: 1.5,
    moveSpeed: -0.3,
  },
  'plate-armor': {
    name: 'Plate Armor',
    icon: 'warrior',
    description: 'Takes 15% less physical damage.',
    harmful: false,
    duration: 0,
    armor: 0.15,
    passive: true,
  },
  'mail-armor': {
    name: 'Mail Armor',
    icon: 'shaman',
    description: 'Takes 10% less physical damage.',
    harmful: false,
    duration: 0,
    armor: 0.1,
    passive: true,
  },
  'leather-armor': {
    name: 'Leather Armor',
    icon: 'rogue',
    description: 'Takes 5% less physical damage.',
    harmful: false,
    duration: 0,
    armor: 0.05,
    passive: true,
  },
  warding: {
    name: 'Warding',
    icon: 'mage',
    description: 'Takes 10% less magic damage.',
    harmful: false,
    duration: 0,
    resistance: 0.1,
    passive: true,
  },
  // Talents: what arrivals and departures leave behind.
  bloodlust: {
    name: 'Bloodlust',
    icon: 'berserker-rage',
    description: 'Dealing 15% more damage.',
    harmful: false,
    duration: 5,
    damageDone: 0.15,
  },
  'piercing-roar': {
    name: 'Piercing Roar',
    icon: 'piercing-howl',
    description: 'Movement slowed by 50%.',
    harmful: true,
    duration: 4,
    moveSpeed: -0.5,
  },
  'defensive-stance': {
    name: 'Defensive Stance',
    icon: 'shield-wall',
    description: 'Absorbs damage.',
    harmful: false,
    duration: 6,
    absorb: 600,
  },
  'presence-of-mind': {
    name: 'Presence of Mind',
    icon: 'fire-blast',
    description: 'Your next Frostbolt, Fireball or Polymorph is instant.',
    harmful: false,
    duration: 15,
    next: { abilities: ['frostbolt', 'fireball', 'polymorph'], instant: true },
  },
  'frost-armor': {
    name: 'Frost Armor',
    icon: 'ice-barrier',
    description: 'Absorbs damage.',
    harmful: false,
    duration: 8,
    dispel: 'magic',
    absorb: 500,
  },
  'parting-frost': {
    name: 'Parting Frost',
    icon: 'frost-nova',
    description: 'Frozen in place.',
    harmful: true,
    duration: 3,
    dispel: 'magic',
    cc: 'root',
    dr: 'root',
    breakOnDamage: 400,
  },
  'inner-focus': {
    name: 'Inner Focus',
    icon: 'flash-heal',
    description: 'Your next Flash Heal or Mind Blast is instant.',
    harmful: false,
    duration: 15,
    next: { abilities: ['flash-heal', 'mind-blast'], instant: true },
  },
  'power-infusion': {
    name: 'Power Infusion',
    icon: 'pain-suppression',
    description: 'Healing done increased by 20%.',
    harmful: false,
    duration: 6,
    dispel: 'magic',
    healingDone: 0.2,
  },
  'parting-shield': {
    name: 'Parting Shield',
    icon: 'power-word-shield',
    description: 'Absorbs damage.',
    harmful: false,
    duration: 10,
    dispel: 'magic',
    absorb: 600,
  },
  'psychic-shriek': {
    name: 'Psychic Shriek',
    icon: 'psychic-scream',
    description: 'Fleeing in terror.',
    harmful: true,
    duration: 2,
    dispel: 'magic',
    cc: 'fear',
    dr: 'fear',
  },
  adrenaline: {
    name: 'Adrenaline',
    icon: 'sprint',
    description: 'Movement speed increased by 70%.',
    harmful: false,
    duration: 3,
    moveSpeed: 0.7,
  },
  ambush: {
    name: 'Ambush',
    icon: 'cheap-shot',
    description: 'Your next Cheap Shot needs no stealth.',
    harmful: false,
    duration: 3,
    next: { abilities: ['cheap-shot'], stealthed: true },
  },
  feint: {
    name: 'Feint',
    icon: 'cloak-of-shadows',
    description: 'Damage taken reduced by 30%.',
    harmful: false,
    duration: 3,
    damageTaken: -0.3,
  },
  'poisoned-exit': {
    name: 'Poisoned Exit',
    icon: 'sinister-strike',
    description: 'Healing received reduced by 20%.',
    harmful: true,
    duration: 6,
    healingTaken: -0.2,
  },
  subtlety: {
    name: 'Master of Subtlety',
    icon: 'stealth',
    description: 'Dealing 10% more damage.',
    harmful: false,
    duration: 4,
    damageDone: 0.1,
  },
  'lock-and-load': {
    name: 'Lock and Load',
    icon: 'aimed-shot',
    description: 'Your next Aimed Shot or Steady Shot is instant.',
    harmful: false,
    duration: 15,
    next: { abilities: ['aimed-shot', 'steady-shot'], instant: true },
  },
  'aspect-of-the-cheetah': {
    name: 'Aspect of the Cheetah',
    icon: 'disengage',
    description: 'Movement speed increased by 40%.',
    harmful: false,
    duration: 3,
    moveSpeed: 0.4,
  },
  'rapid-fire': {
    name: 'Rapid Fire',
    icon: 'steady-shot',
    description: 'Auto-attacks 40% faster.',
    harmful: false,
    duration: 6,
    swingSpeed: 0.4,
  },
  'divine-purpose': {
    name: 'Divine Purpose',
    icon: 'holy-light',
    description: 'Your next Flash of Light or Holy Light is instant.',
    harmful: false,
    duration: 15,
    next: { abilities: ['flash-of-light', 'holy-light'], instant: true },
  },
  'hand-of-freedom': {
    name: 'Hand of Freedom',
    icon: 'blessing-of-freedom',
    description: 'Immune to movement impairing effects.',
    harmful: false,
    duration: 4,
    dispel: 'magic',
    freedom: true,
  },
  'avenging-wrath': {
    name: 'Avenging Wrath',
    icon: 'hammer-of-wrath',
    description: 'Damage and healing done increased by 15%.',
    harmful: false,
    duration: 6,
    dispel: 'magic',
    damageDone: 0.15,
    healingDone: 0.15,
  },
  'natures-swiftness': {
    name: 'Nature’s Swiftness',
    icon: 'regrowth',
    description: 'Your next Druid spell with a cast time is instant.',
    harmful: false,
    duration: 15,
    next: {
      abilities: ['regrowth', 'wrath', 'entangling-roots', 'cyclone'],
      instant: true,
    },
  },
  'bark-shell': {
    name: 'Bark Shell',
    icon: 'barkskin',
    description: 'Damage taken reduced by 20%.',
    harmful: false,
    duration: 4,
    damageTaken: -0.2,
  },
  'elemental-mastery': {
    name: 'Elemental Mastery',
    icon: 'lightning-bolt',
    description: 'Your next Lightning Bolt or Chain Lightning is instant.',
    harmful: false,
    duration: 15,
    next: { abilities: ['lightning-bolt', 'chain-lightning'], instant: true },
  },
  thunderstruck: {
    name: 'Thunderstorm',
    icon: 'chain-lightning',
    description: 'Movement slowed by 30%.',
    harmful: true,
    duration: 3,
    moveSpeed: -0.3,
  },
  // Talents: disciplines.
  juggernaut: {
    name: 'Juggernaut',
    icon: 'shield-wall',
    description: 'Takes 5% less magic damage.',
    harmful: false,
    duration: 0,
    resistance: 0.05,
    passive: true,
  },
  'second-wind': {
    name: 'Second Wind',
    icon: 'berserker-rage',
    description: 'Below 35% health, regenerate 2% of your health per second.',
    harmful: false,
    duration: 0,
    periodic: { interval: 1, heal: 88, school: 'physical' },
    when: { below: 0.35 },
    passive: true,
  },
  berserker: {
    name: 'Berserker',
    icon: 'intimidating-shout',
    description: 'Below 50% health, deal 10% more damage.',
    harmful: false,
    duration: 0,
    damageDone: 0.1,
    when: { below: 0.5 },
    passive: true,
  },
  'glass-cannon': {
    name: 'Glass Cannon',
    icon: 'fireball',
    description: 'Deal 12% more damage and take 5% more.',
    harmful: false,
    duration: 0,
    damageDone: 0.12,
    damageTaken: 0.05,
    passive: true,
  },
  shatter: {
    name: 'Shatter',
    icon: 'ice-lance',
    description: 'Deal 20% more damage to rooted targets.',
    harmful: false,
    duration: 0,
    damageDone: 0.2,
    againstRooted: true,
    passive: true,
  },
  'ice-ward': {
    name: 'Ice Ward',
    icon: 'ice-block',
    description: 'Takes 5% less physical damage.',
    harmful: false,
    duration: 0,
    armor: 0.05,
    passive: true,
  },
  atonement: {
    name: 'Atonement',
    icon: 'shadow-word-pain',
    description: 'Damage you deal as a Priest heals you for 20% of it.',
    harmful: false,
    duration: 0,
    lifesteal: 0.2,
    own: true,
    passive: true,
  },
  martyrdom: {
    name: 'Martyrdom',
    icon: 'mind-flay',
    description: 'Damage never pushes back your Priest casts.',
    harmful: false,
    duration: 0,
    noPushback: true,
    own: true,
    passive: true,
  },
  'borrowed-time': {
    name: 'Borrowed Time',
    icon: 'swap',
    description: 'Swap Class recharges 2 sec faster.',
    harmful: false,
    duration: 0,
    haste: { abilities: ['swap'], by: 0.2 },
    passive: true,
  },
  'fleet-footed': {
    name: 'Fleet Footed',
    icon: 'sprint',
    description: 'Move 8% faster.',
    harmful: false,
    duration: 0,
    moveSpeed: 0.08,
    passive: true,
  },
  'master-of-subtlety': {
    name: 'Master of Subtlety',
    icon: 'stealth',
    description: 'Deal 10% more damage for 4 sec after leaving stealth.',
    harmful: false,
    duration: 0,
    afterStealth: 'subtlety',
    passive: true,
  },
  ruthlessness: {
    name: 'Ruthlessness',
    icon: 'eviscerate',
    description:
      'Each combo point a finisher spends has a 20% chance to come back.',
    harmful: false,
    duration: 0,
    comboBack: 0.2,
    passive: true,
  },
  'steady-focus': {
    name: 'Steady Focus',
    icon: 'steady-shot',
    description: 'While standing still, deal 10% more damage.',
    harmful: false,
    duration: 0,
    damageDone: 0.1,
    when: { still: true },
    passive: true,
  },
  survivalist: {
    name: 'Survivalist',
    icon: 'freezing-trap',
    description: 'Disengage, your traps and Flare recharge 25% faster.',
    harmful: false,
    duration: 0,
    haste: {
      abilities: ['disengage', 'jaw-trap', 'freezing-trap', 'flare'],
      by: 0.25,
    },
    passive: true,
  },
  'hawk-eye': {
    name: 'Hawk Eye',
    icon: 'aimed-shot',
    description: 'Your Hunter abilities reach 5 yards farther.',
    harmful: false,
    duration: 0,
    reach: 5,
    own: true,
    passive: true,
  },
  sanctified: {
    name: 'Sanctified',
    icon: 'flash-of-light',
    description: 'Healing you receive is 10% stronger.',
    harmful: false,
    duration: 0,
    healingTaken: 0.1,
    passive: true,
  },
  illumination: {
    name: 'Illumination',
    icon: 'judgement',
    description: 'Your Paladin spells cost 20% less mana.',
    harmful: false,
    duration: 0,
    thrift: 0.2,
    own: true,
    passive: true,
  },
  'seal-of-light': {
    name: 'Seal of Light',
    icon: 'crusader-strike',
    description: 'Damage you deal as a Paladin heals you for 25% of it.',
    harmful: false,
    duration: 0,
    lifesteal: 0.25,
    own: true,
    passive: true,
  },
  'thick-hide': {
    name: 'Thick Hide',
    icon: 'bear-form',
    description: 'Takes 12% less physical damage.',
    harmful: false,
    duration: 0,
    armor: 0.12,
    passive: true,
  },
  'natures-grace': {
    name: 'Nature’s Grace',
    icon: 'entangling-roots',
    description: 'Roots and snares on you last 25% shorter.',
    harmful: false,
    duration: 0,
    shed: 0.25,
    passive: true,
  },
  shapeshifter: {
    name: 'Shapeshifter',
    icon: 'travel-form',
    description: 'Swapping class no longer ends your shapeshift.',
    harmful: false,
    duration: 0,
    keepsForm: true,
    passive: true,
  },
  'earth-shield': {
    name: 'Earth Shield',
    icon: 'grounding-totem',
    description: 'The first hit you take every 10 sec is halved.',
    harmful: false,
    duration: 0,
    ward: { every: 10, factor: 0.5 },
    passive: true,
  },
  'totemic-endurance': {
    name: 'Totemic Endurance',
    icon: 'tremor-totem',
    description: 'Your totems have 50% more health.',
    harmful: false,
    duration: 0,
    totemHealth: 0.5,
    passive: true,
  },
  'ancestral-spirit': {
    name: 'Ancestral Spirit',
    icon: 'riptide',
    description:
      'Once per match, a killing blow leaves you at 15% health instead.',
    harmful: false,
    duration: 0,
    cheatDeath: 0.15,
    passive: true,
  },
} satisfies Record<string, AuraDef>;

export type AuraId = keyof typeof AURAS;
export const AURA_IDS = Object.keys(AURAS) as AuraId[];

export const ZONES: Record<ZoneId, ZoneDef> = {
  'jaw-trap': {
    radius: 1,
    duration: 60,
    armDelay: 2,
    hidden: true,
    max: 1,
    effects: [
      { type: 'damage', amount: 200 },
      { type: 'aura', aura: 'jaw-trap' },
    ],
  },
  bloom: {
    radius: 5,
    duration: 6,
    pulse: 1,
    friendly: true,
    max: 1,
    effects: [{ type: 'heal', amount: 90 }],
  },
  'healing-stream-totem': {
    totem: 100,
    radius: 8,
    duration: 15,
    pulse: 2,
    friendly: true,
    max: 1,
    effects: [{ type: 'heal', amount: 100 }],
  },
  'earthbind-totem': {
    totem: 100,
    radius: 8,
    duration: 15,
    pulse: 1,
    max: 1,
    effects: [{ type: 'aura', aura: 'earthbind' }],
  },
  'tremor-totem': {
    totem: 100,
    radius: 10,
    duration: 20,
    pulse: 1,
    friendly: true,
    max: 1,
    effects: [{ type: 'breakCc', cc: ['fear'] }],
  },
  'grounding-totem': {
    totem: 100,
    radius: 8,
    duration: 10,
    max: 1,
    grounding: true,
    effects: [],
  },
  'war-banner': {
    totem: 150,
    radius: 8,
    duration: 12,
    pulse: 1,
    friendly: true,
    max: 1,
    effects: [{ type: 'aura', aura: 'rallied' }],
  },
  'rune-of-detonation': {
    radius: 1.5,
    duration: 30,
    armDelay: 1.5,
    max: 2,
    effects: [
      {
        type: 'aoe',
        radius: 6,
        effects: [
          { type: 'damage', amount: 400 },
          { type: 'aura', aura: 'scorched' },
        ],
      },
    ],
  },
  'spirit-lantern': {
    totem: 100,
    radius: 8,
    duration: 12,
    pulse: 3,
    friendly: true,
    max: 1,
    effects: [{ type: 'dispel', count: 1 }],
  },
  caltrops: {
    radius: 5,
    duration: 10,
    pulse: 1,
    max: 1,
    effects: [
      { type: 'damage', amount: 30 },
      { type: 'aura', aura: 'crippled' },
    ],
  },
  'freezing-trap': {
    radius: 1,
    duration: 60,
    armDelay: 2,
    hidden: true,
    max: 1,
    effects: [{ type: 'aura', aura: 'freezing-trap' }],
  },
  flare: {
    radius: 8,
    duration: 10,
    pulse: 1,
    max: 1,
    effects: [{ type: 'reveal' }],
  },
  'hallowed-ground': {
    radius: 6,
    duration: 8,
    pulse: 1,
    max: 1,
    effects: [{ type: 'damage', amount: 70 }],
  },
  // Hidden so the enemy sees one area, not two stacked on the same spot.
  'hallowed-ground-heal': {
    radius: 6,
    duration: 8,
    pulse: 1,
    friendly: true,
    hidden: true,
    max: 1,
    effects: [{ type: 'heal', amount: 70 }],
  },
  consecration: {
    radius: 6,
    duration: 4,
    pulse: 1,
    max: 1,
    effects: [{ type: 'damage', amount: 70 }],
  },
  'consecration-heal': {
    radius: 6,
    duration: 4,
    pulse: 1,
    friendly: true,
    hidden: true,
    max: 1,
    effects: [{ type: 'heal', amount: 70 }],
  },
  thornfield: {
    radius: 6,
    duration: 8,
    pulse: 1,
    max: 1,
    effects: [
      { type: 'damage', amount: 60 },
      { type: 'aura', aura: 'brambles' },
    ],
  },
};

const CROWD_CONTROL: CcType[] = [
  'stun',
  'incapacitate',
  'fear',
  'root',
  'silence',
];

export const ABILITIES = {
  swap: {
    name: 'Swap Class',
    description:
      'Switch to your other class: its spells, resource and auto-attack. Cancels your cast and ends shapeshifts.',
    school: 'physical',
    target: 'self',
    cooldown: 10,
    gcd: 0,
    keepsStealth: true,
    effects: [{ type: 'swap' }],
  },
  // Talents: arrivals fire as Swap Class makes their class the one played, departures as it stops being.
  // Never on a bar; one aimed at an enemy goes off at the caster's target, if it's in range.
  'battle-cry': {
    name: 'Battle Cry',
    icon: 'berserker-rage',
    description: 'Arrive with 30 rage.',
    school: 'physical',
    target: 'self',
    effects: [{ type: 'resource', amount: 30 }],
  },
  intercept: {
    name: 'Intercept',
    icon: 'charge',
    description: 'Charge is ready the moment you arrive.',
    school: 'physical',
    target: 'self',
    effects: [{ type: 'cooldown', ability: 'charge' }],
  },
  bloodlust: {
    name: 'Bloodlust',
    icon: 'berserker-rage',
    description: 'Deal 15% more damage for 5 sec.',
    school: 'physical',
    target: 'self',
    effects: [{ type: 'aura', aura: 'bloodlust', self: true }],
  },
  'piercing-roar': {
    name: 'Piercing Roar',
    icon: 'piercing-howl',
    description: 'Enemies within 8 yards are slowed by 50% for 4 sec.',
    school: 'physical',
    target: 'self',
    effects: [
      {
        type: 'aoe',
        radius: 8,
        effects: [{ type: 'aura', aura: 'piercing-roar' }],
      },
    ],
  },
  'defensive-stance': {
    name: 'Defensive Stance',
    icon: 'shield-wall',
    description: 'Leave with a shield absorbing 600 damage for 6 sec.',
    school: 'physical',
    target: 'self',
    effects: [{ type: 'aura', aura: 'defensive-stance', self: true }],
  },
  'parting-blow': {
    name: 'Parting Blow',
    icon: 'hamstring',
    description: 'Hamstring your target if it is in melee range.',
    school: 'physical',
    target: 'enemy',
    effects: [{ type: 'aura', aura: 'hamstring' }],
  },
  'presence-of-mind': {
    name: 'Presence of Mind',
    icon: 'fire-blast',
    description: 'Your next Frostbolt, Fireball or Polymorph is instant.',
    school: 'arcane',
    target: 'self',
    effects: [{ type: 'aura', aura: 'presence-of-mind', self: true }],
  },
  'frost-armor': {
    name: 'Frost Armor',
    icon: 'ice-barrier',
    description: 'Arrive shielded, absorbing 500 damage for 8 sec.',
    school: 'frost',
    target: 'self',
    effects: [{ type: 'aura', aura: 'frost-armor', self: true }],
  },
  shimmer: {
    name: 'Shimmer',
    icon: 'blink',
    description: 'Blink is ready the moment you arrive.',
    school: 'arcane',
    target: 'self',
    effects: [{ type: 'cooldown', ability: 'blink' }],
  },
  'parting-frost': {
    name: 'Parting Frost',
    icon: 'frost-nova',
    description:
      'Enemies within 8 yards are frozen in place for 3 sec. Shares diminishing returns with roots.',
    school: 'frost',
    target: 'self',
    effects: [
      {
        type: 'aoe',
        radius: 8,
        effects: [{ type: 'aura', aura: 'parting-frost' }],
      },
    ],
  },
  'blink-out': {
    name: 'Blink Out',
    icon: 'blink',
    description: 'Blink 10 yards forward as you leave.',
    school: 'arcane',
    target: 'self',
    effects: [{ type: 'blink', distance: 10 }],
  },
  displacement: {
    name: 'Displacement',
    icon: 'rune-of-detonation',
    description: 'Etch a Rune of Detonation at your feet.',
    school: 'fire',
    target: 'self',
    effects: [{ type: 'zone', zone: 'rune-of-detonation' }],
  },
  renewal: {
    name: 'Renewal',
    icon: 'renew',
    description: 'Arrive with Renew on yourself.',
    school: 'holy',
    target: 'self',
    effects: [{ type: 'aura', aura: 'renew', self: true }],
  },
  'inner-focus': {
    name: 'Inner Focus',
    icon: 'flash-heal',
    description: 'Your next Flash Heal or Mind Blast is instant.',
    school: 'holy',
    target: 'self',
    effects: [{ type: 'aura', aura: 'inner-focus', self: true }],
  },
  'power-infusion': {
    name: 'Power Infusion',
    icon: 'pain-suppression',
    description: 'Your healing is 20% stronger for 6 sec.',
    school: 'holy',
    target: 'self',
    effects: [{ type: 'aura', aura: 'power-infusion', self: true }],
  },
  'parting-shield': {
    name: 'Parting Shield',
    icon: 'power-word-shield',
    description: 'Leave with a shield absorbing 600 damage for 10 sec.',
    school: 'holy',
    target: 'self',
    effects: [{ type: 'aura', aura: 'parting-shield', self: true }],
  },
  'psychic-shriek': {
    name: 'Psychic Shriek',
    icon: 'psychic-scream',
    description:
      'Enemies within 6 yards flee for 2 sec. Shares diminishing returns with fears.',
    school: 'shadow',
    target: 'self',
    effects: [
      {
        type: 'aoe',
        radius: 6,
        effects: [{ type: 'aura', aura: 'psychic-shriek' }],
      },
    ],
  },
  fade: {
    name: 'Fade',
    icon: 'mind-flay',
    description:
      'Enemies stop auto-attacking you, and any spell they are casting at you fails.',
    school: 'shadow',
    target: 'self',
    effects: [{ type: 'shake' }],
  },
  shadowstep: {
    name: 'Shadowstep',
    icon: 'stealth',
    description: 'Out of combat, arrive in stealth.',
    school: 'physical',
    target: 'self',
    when: { outOfCombat: true },
    effects: [{ type: 'aura', aura: 'stealth', self: true }],
  },
  adrenaline: {
    name: 'Adrenaline',
    icon: 'sprint',
    description: 'Move 70% faster for 3 sec.',
    school: 'physical',
    target: 'self',
    effects: [{ type: 'aura', aura: 'adrenaline', self: true }],
  },
  ambush: {
    name: 'Ambush',
    icon: 'cheap-shot',
    description: 'For 3 sec, you can Cheap Shot without stealth.',
    school: 'physical',
    target: 'self',
    effects: [{ type: 'aura', aura: 'ambush', self: true }],
  },
  'smoke-bomb': {
    name: 'Smoke Bomb',
    icon: 'caltrops',
    description: 'Scatter caltrops at your feet.',
    school: 'physical',
    target: 'self',
    effects: [{ type: 'zone', zone: 'caltrops' }],
  },
  feint: {
    name: 'Feint',
    icon: 'cloak-of-shadows',
    description: 'Take 30% less damage for 3 sec.',
    school: 'physical',
    target: 'self',
    effects: [{ type: 'aura', aura: 'feint', self: true }],
  },
  'poisoned-exit': {
    name: 'Poisoned Exit',
    icon: 'sinister-strike',
    description:
      'Your target, within 30 yards, receives 20% less healing for 6 sec.',
    school: 'nature',
    target: 'enemy',
    range: 30,
    effects: [{ type: 'aura', aura: 'poisoned-exit' }],
  },
  'lock-and-load': {
    name: 'Lock and Load',
    icon: 'aimed-shot',
    description: 'Your next Aimed Shot or Steady Shot is instant.',
    school: 'physical',
    target: 'self',
    effects: [{ type: 'aura', aura: 'lock-and-load', self: true }],
  },
  'aspect-of-the-cheetah': {
    name: 'Aspect of the Cheetah',
    icon: 'disengage',
    description: 'Move 40% faster for 3 sec.',
    school: 'nature',
    target: 'self',
    effects: [{ type: 'aura', aura: 'aspect-of-the-cheetah', self: true }],
  },
  'rapid-fire': {
    name: 'Rapid Fire',
    icon: 'steady-shot',
    description: 'Your auto-attacks come 40% faster for 6 sec.',
    school: 'physical',
    target: 'self',
    effects: [{ type: 'aura', aura: 'rapid-fire', self: true }],
  },
  'parting-leap': {
    name: 'Parting Leap',
    icon: 'disengage',
    description: 'Leap backward as you leave.',
    school: 'physical',
    target: 'self',
    effects: [{ type: 'leap', distance: -12, height: 1.5 }],
  },
  'parting-trap': {
    name: 'Parting Trap',
    icon: 'jaw-trap',
    description: 'Hide a Jaw Trap at your feet.',
    school: 'physical',
    target: 'self',
    effects: [{ type: 'zone', zone: 'jaw-trap' }],
  },
  'flash-flare': {
    name: 'Flash Flare',
    icon: 'flare',
    description: 'Drop a Flare at your feet, revealing stealth around you.',
    school: 'arcane',
    target: 'self',
    effects: [{ type: 'zone', zone: 'flare' }],
  },
  'divine-purpose': {
    name: 'Divine Purpose',
    icon: 'holy-light',
    description: 'Your next Flash of Light or Holy Light is instant.',
    school: 'holy',
    target: 'self',
    effects: [{ type: 'aura', aura: 'divine-purpose', self: true }],
  },
  'hand-of-freedom': {
    name: 'Hand of Freedom',
    icon: 'blessing-of-freedom',
    description: 'Immune to roots and snares for 4 sec.',
    school: 'holy',
    target: 'self',
    effects: [{ type: 'aura', aura: 'hand-of-freedom', self: true }],
  },
  'avenging-wrath': {
    name: 'Avenging Wrath',
    icon: 'hammer-of-wrath',
    description: 'Deal 15% more damage and healing for 6 sec.',
    school: 'holy',
    target: 'self',
    effects: [{ type: 'aura', aura: 'avenging-wrath', self: true }],
  },
  purify: {
    name: 'Purify',
    icon: 'cleanse',
    description: 'Remove one harmful magic effect from yourself.',
    school: 'holy',
    target: 'self',
    effects: [{ type: 'dispel', count: 1 }],
  },
  'lay-on-hands': {
    name: 'Lay on Hands',
    icon: 'holy-light',
    description: 'Below 30% health, heal yourself for 1500.',
    school: 'holy',
    target: 'self',
    when: { below: 0.3 },
    effects: [{ type: 'heal', amount: 1500 }],
  },
  'consecrated-exit': {
    name: 'Consecrated Exit',
    icon: 'hallowed-ground',
    description:
      'Hallow the ground at your feet for 4 sec: enemies in it take holy damage, allies are healed.',
    school: 'holy',
    target: 'self',
    effects: [
      { type: 'zone', zone: 'consecration' },
      { type: 'zone', zone: 'consecration-heal' },
    ],
  },
  'rejuvenating-arrival': {
    name: 'Rejuvenating Arrival',
    icon: 'rejuvenation',
    description: 'Arrive with Rejuvenation on yourself.',
    school: 'nature',
    target: 'self',
    effects: [{ type: 'aura', aura: 'rejuvenation', self: true }],
  },
  'natures-swiftness': {
    name: 'Nature’s Swiftness',
    icon: 'regrowth',
    description: 'Your next Druid spell with a cast time is instant.',
    school: 'nature',
    target: 'self',
    effects: [{ type: 'aura', aura: 'natures-swiftness', self: true }],
  },
  'feral-instinct': {
    name: 'Feral Instinct',
    icon: 'bear-form',
    description: 'Arrive already in Bear Form.',
    school: 'nature',
    target: 'self',
    effects: [{ type: 'aura', aura: 'bear-form', self: true }],
  },
  'bramble-exit': {
    name: 'Bramble Exit',
    icon: 'thornfield',
    description: 'Raise a Thornfield at your feet.',
    school: 'nature',
    target: 'self',
    effects: [{ type: 'zone', zone: 'thornfield' }],
  },
  'bark-shell': {
    name: 'Bark Shell',
    icon: 'barkskin',
    description: 'Take 20% less damage for 4 sec.',
    school: 'nature',
    target: 'self',
    effects: [{ type: 'aura', aura: 'bark-shell', self: true }],
  },
  'shed-skin': {
    name: 'Shed Skin',
    icon: 'travel-form',
    description: 'Break free of roots and snares.',
    school: 'nature',
    target: 'self',
    effects: [{ type: 'breakCc', cc: ['root'] }, { type: 'removeSnares' }],
  },
  'tidal-arrival': {
    name: 'Tidal Arrival',
    icon: 'riptide',
    description: 'Arrive with Riptide’s healing over time on yourself.',
    school: 'nature',
    target: 'self',
    effects: [{ type: 'aura', aura: 'riptide', self: true }],
  },
  'elemental-mastery': {
    name: 'Elemental Mastery',
    icon: 'lightning-bolt',
    description: 'Your next Lightning Bolt or Chain Lightning is instant.',
    school: 'nature',
    target: 'self',
    effects: [{ type: 'aura', aura: 'elemental-mastery', self: true }],
  },
  'totemic-recall': {
    name: 'Totemic Recall',
    icon: 'healing-stream-totem',
    description: 'Healing Stream Totem is ready the moment you arrive.',
    school: 'nature',
    target: 'self',
    effects: [{ type: 'cooldown', ability: 'healing-stream-totem' }],
  },
  'grounded-exit': {
    name: 'Grounded Exit',
    icon: 'grounding-totem',
    description: 'Plant a Grounding Totem at your feet.',
    school: 'nature',
    target: 'self',
    effects: [{ type: 'zone', zone: 'grounding-totem' }],
  },
  'earthbind-exit': {
    name: 'Earthbind Exit',
    icon: 'earthbind-totem',
    description: 'Plant an Earthbind Totem at your feet.',
    school: 'nature',
    target: 'self',
    effects: [{ type: 'zone', zone: 'earthbind-totem' }],
  },
  thunderstorm: {
    name: 'Thunderstorm',
    icon: 'chain-lightning',
    description:
      'Enemies within 8 yards take 200 nature damage and are slowed by 30% for 3 sec.',
    school: 'nature',
    target: 'self',
    effects: [
      {
        type: 'aoe',
        radius: 8,
        effects: [
          { type: 'damage', amount: 200 },
          { type: 'aura', aura: 'thunderstruck' },
        ],
      },
    ],
  },
  'pvp-trinket': {
    name: "Gladiator's Medallion",
    description:
      'Removes all movement impairing effects and all stuns, incapacitates, fears, roots and silences.',
    school: 'physical',
    target: 'self',
    cooldown: 120,
    gcd: 0,
    usableWhileCc: true,
    keepsStealth: true,
    effects: [{ type: 'breakCc', cc: CROWD_CONTROL }, { type: 'removeSnares' }],
  },
  bandage: {
    name: 'Bandage',
    description:
      'Heals yourself or a friend for 1800 over 6 sec. Damage interrupts it. The target is Recently Bandaged for 60 sec.',
    school: 'physical',
    target: 'friendly',
    channel: { duration: 6, ticks: 6 },
    range: 15,
    exhaustion: 'recently-bandaged',
    interruptedByDamage: true,
    effects: [{ type: 'heal', amount: 300 }],
  },
  'healing-potion': {
    name: 'Healing Potion',
    description: 'Instantly heals you for 1000. Three uses per match.',
    school: 'physical',
    target: 'self',
    cooldown: 60,
    gcd: 0,
    uses: 3,
    effects: [{ type: 'heal', amount: 1000 }],
  },
  'regeneration-potion': {
    name: 'Regeneration Potion',
    description: 'Heals you for 2000 over 15 sec.',
    school: 'physical',
    target: 'self',
    cooldown: 120,
    gcd: 0,
    effects: [{ type: 'aura', aura: 'regeneration-potion' }],
  },
  refreshment: {
    name: 'Refreshment',
    description:
      'Eat and drink, restoring 3000 health and 7200 mana over 12 sec. Only out of combat; moving or taking damage stops it.',
    school: 'physical',
    target: 'self',
    channel: { duration: 12, ticks: 6 },
    outOfCombatOnly: true,
    interruptedByDamage: true,
    effects: [
      { type: 'heal', amount: 500 },
      { type: 'mana', amount: 1200 },
    ],
  },
  'mortal-strike': {
    name: 'Mortal Strike',
    description: 'A vicious strike that reduces healing received by 35%.',
    school: 'physical',
    target: 'enemy',
    cost: 30,
    cooldown: 6,
    effects: [
      { type: 'damage', amount: 520 },
      { type: 'aura', aura: 'mortal-wound' },
    ],
  },
  hamstring: {
    name: 'Hamstring',
    description: 'Maims the target, slowing it by 50%.',
    school: 'physical',
    target: 'enemy',
    cost: 10,
    effects: [
      { type: 'damage', amount: 50 },
      { type: 'aura', aura: 'hamstring' },
    ],
  },
  rend: {
    name: 'Rend',
    description: 'Wounds the target, causing it to bleed over 15 sec.',
    school: 'physical',
    target: 'enemy',
    cost: 10,
    effects: [{ type: 'aura', aura: 'rend' }],
  },
  charge: {
    name: 'Charge',
    description: 'Charge an enemy, rooting it and generating 20 rage.',
    school: 'physical',
    target: 'enemy',
    range: 25,
    minRange: 6,
    cooldown: 15,
    gcd: 0,
    effects: [
      { type: 'charge', speed: 28 },
      { type: 'aura', aura: 'charge-root' },
      { type: 'resource', amount: 20 },
    ],
  },
  pummel: {
    name: 'Pummel',
    description: 'Interrupts spellcasting, locking out that school for 4 sec.',
    school: 'physical',
    target: 'enemy',
    cost: 10,
    cooldown: 15,
    gcd: 0,
    effects: [{ type: 'interrupt', lockout: 4 }],
  },
  'intimidating-shout': {
    name: 'Intimidating Shout',
    description: 'Nearby enemies flee in terror for 6 sec.',
    school: 'physical',
    target: 'self',
    cooldown: 90,
    effects: [
      {
        type: 'aoe',
        radius: 8,
        effects: [{ type: 'aura', aura: 'intimidating-shout' }],
      },
    ],
  },
  'berserker-rage': {
    name: 'Berserker Rage',
    description: 'Breaks fear and incapacitate effects and generates 20 rage.',
    school: 'physical',
    target: 'self',
    cooldown: 30,
    gcd: 0,
    usableWhileCc: true,
    effects: [
      { type: 'breakCc', cc: ['fear', 'incapacitate'] },
      { type: 'resource', amount: 20 },
    ],
  },
  'shield-wall': {
    name: 'Shield Wall',
    description: 'Reduces damage taken by 40% for 8 sec.',
    school: 'physical',
    target: 'self',
    cooldown: 120,
    gcd: 0,
    effects: [{ type: 'aura', aura: 'shield-wall', self: true }],
  },
  'spell-reflection': {
    name: 'Spell Reflection',
    description:
      'For 5 sec, the next spell an enemy casts on you is reflected back at them.',
    school: 'physical',
    target: 'self',
    cost: 15,
    cooldown: 25,
    gcd: 0,
    effects: [{ type: 'aura', aura: 'spell-reflection', self: true }],
  },
  'piercing-howl': {
    name: 'Piercing Howl',
    description: 'Slows nearby enemies by 50% for 6 sec.',
    school: 'physical',
    target: 'self',
    cost: 10,
    cooldown: 30,
    effects: [
      {
        type: 'aoe',
        radius: 10,
        effects: [{ type: 'aura', aura: 'piercing-howl' }],
      },
    ],
  },
  'war-banner': {
    name: 'War Banner',
    description:
      'Plants a banner at your feet for 12 sec. Allies within 8 yards take 15% less damage and are immune to roots and snares.',
    school: 'physical',
    target: 'self',
    cooldown: 90,
    effects: [{ type: 'zone', zone: 'war-banner' }],
  },
  execute: {
    name: 'Execute',
    description: 'Finish off an enemy below 20% health.',
    school: 'physical',
    target: 'enemy',
    cost: 20,
    executeBelow: 0.2,
    effects: [{ type: 'damage', amount: 900 }],
  },
  frostbolt: {
    name: 'Frostbolt',
    description: 'Frost damage that slows the target by 40%.',
    school: 'frost',
    target: 'enemy',
    cost: 300,
    castTime: 1.8,
    range: 30,
    projectileSpeed: 26.25,
    effects: [
      { type: 'damage', amount: 450 },
      { type: 'aura', aura: 'chilled' },
    ],
  },
  fireball: {
    name: 'Fireball',
    description: 'Hurls a ball of fire that burns the target for 8 sec.',
    school: 'fire',
    target: 'enemy',
    cost: 400,
    castTime: 2.5,
    range: 30,
    projectileSpeed: 22.5,
    effects: [
      { type: 'damage', amount: 550 },
      { type: 'aura', aura: 'fireball' },
    ],
  },
  'fire-blast': {
    name: 'Fire Blast',
    description: 'Instant fire damage.',
    school: 'fire',
    target: 'enemy',
    cost: 200,
    cooldown: 10,
    range: 25,
    effects: [{ type: 'damage', amount: 300 }],
  },
  polymorph: {
    name: 'Polymorph',
    description:
      'Transforms the enemy, incapacitating it for 8 sec. Damage breaks it.',
    school: 'arcane',
    target: 'enemy',
    cost: 300,
    castTime: 1.5,
    range: 30,
    effects: [{ type: 'aura', aura: 'polymorph' }],
  },
  'frost-nova': {
    name: 'Frost Nova',
    description: 'Freezes nearby enemies in place for 6 sec.',
    school: 'frost',
    target: 'self',
    cost: 200,
    cooldown: 25,
    effects: [
      {
        type: 'aoe',
        radius: 8,
        effects: [{ type: 'aura', aura: 'frost-nova' }],
      },
    ],
  },
  counterspell: {
    name: 'Counterspell',
    description: 'Interrupts spellcasting, locking out that school for 5 sec.',
    school: 'arcane',
    target: 'enemy',
    cost: 100,
    cooldown: 24,
    gcd: 0,
    range: 30,
    effects: [{ type: 'interrupt', lockout: 5 }],
  },
  blink: {
    name: 'Blink',
    description: 'Teleports you forward, freeing you from stuns and roots.',
    school: 'arcane',
    target: 'self',
    cost: 100,
    cooldown: 15,
    gcd: 0,
    usableWhileCc: true,
    effects: [
      { type: 'breakCc', cc: ['stun', 'root'] },
      { type: 'blink', distance: 15 },
    ],
  },
  'ice-barrier': {
    name: 'Ice Barrier',
    description: 'Shields you, absorbing 900 damage for 30 sec.',
    school: 'frost',
    target: 'self',
    cost: 300,
    cooldown: 30,
    effects: [{ type: 'aura', aura: 'ice-barrier', self: true }],
  },
  hailstorm: {
    name: 'Hailstorm',
    description:
      'Calls down hail on an area for 4 sec, dealing frost damage every second and slowing enemies inside by 40%.',
    school: 'frost',
    target: 'ground',
    cost: 500,
    channel: { duration: 4, ticks: 4 },
    range: 30,
    effects: [
      {
        type: 'aoe',
        radius: 6,
        effects: [
          { type: 'damage', amount: 130 },
          { type: 'aura', aura: 'chilled' },
        ],
      },
    ],
  },
  'ice-block': {
    name: 'Ice Block',
    description:
      'Immune to everything for 10 sec, but unable to act. Cast again to cancel.',
    school: 'frost',
    target: 'self',
    cooldown: 180,
    gcd: 0,
    usableWhileCc: true,
    cancelable: true,
    effects: [
      { type: 'cleanse' },
      { type: 'aura', aura: 'ice-block', self: true },
    ],
  },
  'ice-lance': {
    name: 'Ice Lance',
    description: 'An instant shard of ice, thrown on the move.',
    school: 'frost',
    target: 'enemy',
    cost: 150,
    range: 30,
    projectileSpeed: 30,
    effects: [{ type: 'damage', amount: 220 }],
  },
  'rune-of-detonation': {
    name: 'Rune of Detonation',
    description:
      'Etches a rune on the ground for 30 sec that arms after 1.5 sec. The first enemy to step on it sets it off, burning every enemy within 6 yards and slowing them by 40% for 4 sec. Up to two at once.',
    school: 'fire',
    target: 'ground',
    cost: 300,
    cooldown: 12,
    range: 30,
    effects: [{ type: 'zone', zone: 'rune-of-detonation' }],
  },
  'flash-heal': {
    name: 'Flash Heal',
    description: 'A fast heal.',
    school: 'holy',
    target: 'friendly',
    cost: 500,
    castTime: 1.5,
    range: 40,
    effects: [{ type: 'heal', amount: 750 }],
  },
  renew: {
    name: 'Renew',
    description: 'Heals the target over 15 sec.',
    school: 'holy',
    target: 'friendly',
    cost: 300,
    range: 40,
    effects: [{ type: 'aura', aura: 'renew' }],
  },
  'power-word-shield': {
    name: 'Power Word: Shield',
    description: 'Shields the target, absorbing 800 damage.',
    school: 'holy',
    target: 'friendly',
    cost: 400,
    cooldown: 7,
    range: 40,
    effects: [{ type: 'aura', aura: 'power-word-shield' }],
  },
  'pain-suppression': {
    name: 'Pain Suppression',
    description: "Reduces a friend's damage taken by 40% for 8 sec.",
    school: 'holy',
    target: 'friendly',
    cost: 150,
    cooldown: 120,
    gcd: 0,
    range: 40,
    effects: [{ type: 'aura', aura: 'pain-suppression' }],
  },
  'shadow-word-pain': {
    name: 'Shadow Word: Pain',
    description: 'Shadow damage over 18 sec.',
    school: 'shadow',
    target: 'enemy',
    cost: 200,
    range: 40,
    effects: [{ type: 'aura', aura: 'shadow-word-pain' }],
  },
  'mind-blast': {
    name: 'Mind Blast',
    description: 'Blasts the target with shadow damage.',
    school: 'shadow',
    target: 'enemy',
    cost: 300,
    castTime: 1.5,
    cooldown: 8,
    range: 40,
    effects: [{ type: 'damage', amount: 550 }],
  },
  'mind-flay': {
    name: 'Mind Flay',
    description: 'Channels shadow damage over 3 sec, slowing the target.',
    school: 'shadow',
    target: 'enemy',
    cost: 200,
    channel: { duration: 3, ticks: 3 },
    range: 40,
    effects: [
      { type: 'damage', amount: 150 },
      { type: 'aura', aura: 'mind-flay' },
    ],
  },
  'psychic-scream': {
    name: 'Psychic Scream',
    description: 'Nearby enemies flee in terror for 6 sec.',
    school: 'shadow',
    target: 'self',
    cost: 300,
    cooldown: 30,
    effects: [
      {
        type: 'aoe',
        radius: 8,
        effects: [{ type: 'aura', aura: 'psychic-scream' }],
      },
    ],
  },
  'dispel-magic': {
    name: 'Dispel Magic',
    description:
      'Removes one harmful magic effect from a friend or one helpful one from an enemy.',
    school: 'holy',
    target: 'any',
    cost: 300,
    cooldown: 8,
    range: 30,
    effects: [{ type: 'dispel', count: 1 }],
  },
  silence: {
    name: 'Silence',
    description: 'Silences the target for 4 sec, interrupting spellcasting.',
    school: 'shadow',
    target: 'enemy',
    cost: 150,
    cooldown: 45,
    gcd: 0,
    range: 30,
    effects: [{ type: 'aura', aura: 'silence' }],
  },
  'mass-dispel': {
    name: 'Mass Dispel',
    description:
      'Removes one harmful magic effect from every ally and one helpful magic effect from every enemy in an area.',
    school: 'holy',
    target: 'ground',
    cost: 600,
    castTime: 1.5,
    cooldown: 30,
    range: 30,
    effects: [
      {
        type: 'aoe',
        radius: 8,
        friendly: true,
        effects: [{ type: 'dispel', count: 1 }],
      },
      { type: 'aoe', radius: 8, effects: [{ type: 'dispel', count: 1 }] },
    ],
  },
  'spirit-lantern': {
    name: 'Spirit Lantern',
    description:
      'Raises a lantern at your feet for 12 sec. Every 3 sec it removes one harmful magic effect from each ally within 8 yards.',
    school: 'holy',
    target: 'self',
    cost: 300,
    cooldown: 45,
    effects: [{ type: 'zone', zone: 'spirit-lantern' }],
  },
  stealth: {
    name: 'Stealth',
    description:
      'Become invisible to enemies. Only out of combat. Click the aura off to leave stealth.',
    school: 'physical',
    target: 'self',
    cooldown: 2,
    gcd: 0,
    outOfCombatOnly: true,
    keepsStealth: true,
    cancelable: true,
    effects: [{ type: 'aura', aura: 'stealth', self: true }],
  },
  'cheap-shot': {
    name: 'Cheap Shot',
    description:
      'Stuns the target for 4 sec. Requires stealth. Awards 2 combo points.',
    school: 'physical',
    target: 'enemy',
    cost: 40,
    requiresStealth: true,
    combo: 2,
    effects: [{ type: 'aura', aura: 'cheap-shot' }],
  },
  'sinister-strike': {
    name: 'Sinister Strike',
    description: 'A strike that awards a combo point.',
    school: 'physical',
    target: 'enemy',
    cost: 40,
    combo: 1,
    effects: [{ type: 'damage', amount: 280 }],
  },
  eviscerate: {
    name: 'Eviscerate',
    description: 'Finisher: damage per combo point.',
    school: 'physical',
    target: 'enemy',
    cost: 35,
    finisher: true,
    effects: [{ type: 'damage', amount: 100, perCombo: 200 }],
  },
  'kidney-shot': {
    name: 'Kidney Shot',
    description: 'Finisher: stuns for 1 sec plus 1 sec per combo point.',
    school: 'physical',
    target: 'enemy',
    cost: 25,
    cooldown: 20,
    finisher: true,
    effects: [{ type: 'aura', aura: 'kidney-shot', perCombo: 1 }],
  },
  kick: {
    name: 'Kick',
    description: 'Interrupts spellcasting, locking out that school for 5 sec.',
    school: 'physical',
    target: 'enemy',
    cost: 15,
    cooldown: 15,
    gcd: 0,
    effects: [{ type: 'interrupt', lockout: 5 }],
  },
  gouge: {
    name: 'Gouge',
    description: 'Incapacitates the target for 4 sec. Damage breaks it.',
    school: 'physical',
    target: 'enemy',
    cost: 25,
    cooldown: 15,
    effects: [{ type: 'aura', aura: 'gouge' }],
  },
  vanish: {
    name: 'Vanish',
    description:
      'Drops from sight even in combat, shedding roots and snares. Damage does not break it for 2 sec.',
    school: 'physical',
    target: 'self',
    cooldown: 120,
    gcd: 0,
    keepsStealth: true,
    effects: [
      { type: 'breakCc', cc: ['root'] },
      { type: 'removeSnares' },
      { type: 'aura', aura: 'stealth', self: true },
      { type: 'aura', aura: 'vanish', self: true },
    ],
  },
  sprint: {
    name: 'Sprint',
    description: 'Increases movement speed by 70% for 8 sec.',
    school: 'physical',
    target: 'self',
    cooldown: 60,
    gcd: 0,
    keepsStealth: true,
    effects: [{ type: 'aura', aura: 'sprint', self: true }],
  },
  blind: {
    name: 'Blind',
    description:
      'Blinds the target, incapacitating it for 8 sec. Damage breaks it.',
    school: 'physical',
    target: 'enemy',
    cost: 30,
    cooldown: 90,
    range: 15,
    effects: [{ type: 'aura', aura: 'blind' }],
  },
  'cloak-of-shadows': {
    name: 'Cloak of Shadows',
    description:
      'Sheds every harmful magic effect and makes you immune to spells for 5 sec.',
    school: 'physical',
    target: 'self',
    cooldown: 90,
    gcd: 0,
    keepsStealth: true,
    effects: [
      { type: 'dispel', count: Infinity },
      { type: 'aura', aura: 'cloak-of-shadows', self: true },
    ],
  },
  caltrops: {
    name: 'Caltrops',
    description:
      'Scatters caltrops over an area for 10 sec. Enemies inside take damage every second and are slowed by 50%.',
    school: 'physical',
    target: 'ground',
    cost: 30,
    cooldown: 30,
    range: 20,
    effects: [{ type: 'zone', zone: 'caltrops' }],
  },
  'steady-shot': {
    name: 'Steady Shot',
    description: 'A steady shot.',
    school: 'physical',
    target: 'enemy',
    cost: 150,
    castTime: 1.5,
    range: 35,
    projectileSpeed: 45,
    effects: [{ type: 'damage', amount: 380 }],
  },
  'arcane-shot': {
    name: 'Arcane Shot',
    description: 'An instant shot of arcane damage.',
    school: 'arcane',
    target: 'enemy',
    cost: 250,
    cooldown: 6,
    range: 35,
    projectileSpeed: 45,
    effects: [{ type: 'damage', amount: 300 }],
  },
  'aimed-shot': {
    name: 'Aimed Shot',
    description: 'A powerful shot that reduces healing received by 35%.',
    school: 'physical',
    target: 'enemy',
    cost: 350,
    castTime: 2.5,
    cooldown: 10,
    range: 35,
    projectileSpeed: 45,
    effects: [
      { type: 'damage', amount: 650 },
      { type: 'aura', aura: 'aimed-shot' },
    ],
  },
  'serpent-sting': {
    name: 'Serpent Sting',
    description: 'Nature damage over 15 sec.',
    school: 'nature',
    target: 'enemy',
    cost: 200,
    range: 35,
    effects: [{ type: 'aura', aura: 'serpent-sting' }],
  },
  'concussive-shot': {
    name: 'Concussive Shot',
    description: 'Dazes the target, slowing it by 50% for 4 sec.',
    school: 'arcane',
    target: 'enemy',
    cost: 100,
    cooldown: 12,
    range: 35,
    projectileSpeed: 45,
    effects: [{ type: 'aura', aura: 'concussive-shot' }],
  },
  'scatter-shot': {
    name: 'Scatter Shot',
    description: 'Disorients the target for 4 sec. Damage breaks it.',
    school: 'physical',
    target: 'enemy',
    cost: 200,
    cooldown: 30,
    range: 15,
    effects: [{ type: 'aura', aura: 'scatter-shot' }],
  },
  'silencing-shot': {
    name: 'Silencing Shot',
    description:
      'Silences the target for 3 sec, interrupting spellcasting and locking out that school.',
    school: 'physical',
    target: 'enemy',
    cost: 150,
    cooldown: 20,
    gcd: 0,
    range: 35,
    effects: [
      { type: 'interrupt', lockout: 3 },
      { type: 'aura', aura: 'silencing-shot' },
    ],
  },
  'tranquilizing-shot': {
    name: 'Tranquilizing Shot',
    description: 'Removes one helpful magic effect from the enemy.',
    school: 'nature',
    target: 'enemy',
    cost: 200,
    cooldown: 8,
    range: 35,
    effects: [{ type: 'dispel', count: 1 }],
  },
  disengage: {
    name: 'Disengage',
    description: 'Leaps backward, out of melee range.',
    school: 'physical',
    target: 'self',
    cooldown: 25,
    gcd: 0,
    effects: [{ type: 'leap', distance: -12, height: 1.5 }],
  },
  'jaw-trap': {
    name: 'Jaw Trap',
    description:
      'Hides a trap at your feet that arms after 2 sec. The first enemy to step on it takes damage, is rooted for 4 sec and is pulled out of stealth.',
    school: 'physical',
    target: 'self',
    cost: 200,
    cooldown: 20,
    effects: [{ type: 'zone', zone: 'jaw-trap' }],
  },
  'freezing-trap': {
    name: 'Freezing Trap',
    description:
      'Throws a hidden trap that arms after 2 sec. The first enemy to step on it is frozen solid for 8 sec and pulled out of stealth. Damage breaks it.',
    school: 'frost',
    target: 'ground',
    cost: 200,
    cooldown: 30,
    range: 30,
    effects: [{ type: 'zone', zone: 'freezing-trap' }],
  },
  flare: {
    name: 'Flare',
    description:
      'Lights up an area for 10 sec, pulling enemies inside out of stealth at once and every second after.',
    school: 'arcane',
    target: 'ground',
    cost: 100,
    cooldown: 20,
    range: 35,
    effects: [
      { type: 'aoe', radius: 8, effects: [{ type: 'reveal' }] },
      { type: 'zone', zone: 'flare' },
    ],
  },
  'crusader-strike': {
    name: 'Crusader Strike',
    description: 'An instant strike that deals weapon damage.',
    school: 'physical',
    target: 'enemy',
    cost: 250,
    cooldown: 6,
    effects: [{ type: 'damage', amount: 420 }],
  },
  judgement: {
    name: 'Judgement',
    description: 'Instant holy damage at short range.',
    school: 'holy',
    target: 'enemy',
    cost: 200,
    cooldown: 8,
    range: 10,
    effects: [{ type: 'damage', amount: 320 }],
  },
  'hammer-of-wrath': {
    name: 'Hammer of Wrath',
    description: 'Hurls a hammer at an enemy below 20% health.',
    school: 'holy',
    target: 'enemy',
    cost: 300,
    cooldown: 6,
    range: 30,
    executeBelow: 0.2,
    projectileSpeed: 30,
    effects: [{ type: 'damage', amount: 650 }],
  },
  'hammer-of-justice': {
    name: 'Hammer of Justice',
    description: 'Stuns the target for 5 sec.',
    school: 'holy',
    target: 'enemy',
    cost: 150,
    cooldown: 60,
    range: 10,
    effects: [{ type: 'aura', aura: 'hammer-of-justice' }],
  },
  'flash-of-light': {
    name: 'Flash of Light',
    description: 'A quick, efficient heal.',
    school: 'holy',
    target: 'friendly',
    cost: 250,
    castTime: 1.5,
    range: 40,
    effects: [{ type: 'heal', amount: 450 }],
  },
  'holy-light': {
    name: 'Holy Light',
    description: 'A slow, powerful heal.',
    school: 'holy',
    target: 'friendly',
    cost: 700,
    castTime: 2.5,
    range: 40,
    effects: [{ type: 'heal', amount: 1100 }],
  },
  cleanse: {
    name: 'Cleanse',
    description: 'Removes one harmful magic effect from a friend.',
    school: 'holy',
    target: 'friendly',
    cost: 300,
    cooldown: 8,
    range: 40,
    effects: [{ type: 'dispel', count: 1 }],
  },
  'blessing-of-freedom': {
    name: 'Blessing of Freedom',
    description: 'Makes a friend immune to roots and snares for 6 sec.',
    school: 'holy',
    target: 'friendly',
    cost: 150,
    cooldown: 25,
    range: 40,
    effects: [{ type: 'aura', aura: 'blessing-of-freedom' }],
  },
  'divine-shield': {
    name: 'Divine Shield',
    description:
      'Immune to all damage and harmful effects for 8 sec. Cast again to cancel.',
    school: 'holy',
    target: 'self',
    cooldown: 180,
    gcd: 0,
    cancelable: true,
    effects: [
      { type: 'cleanse' },
      { type: 'aura', aura: 'divine-shield', self: true },
    ],
  },
  repentance: {
    name: 'Repentance',
    description:
      'Puts the enemy into a trance, incapacitating it for 6 sec. Damage breaks it.',
    school: 'holy',
    target: 'enemy',
    cost: 250,
    castTime: 1.5,
    cooldown: 30,
    range: 30,
    effects: [{ type: 'aura', aura: 'repentance' }],
  },
  'blessing-of-protection': {
    name: 'Blessing of Protection',
    description:
      'Makes a friend immune to physical damage and effects for 8 sec. Not again on the same friend for 30 sec.',
    school: 'holy',
    target: 'friendly',
    cost: 150,
    cooldown: 120,
    range: 40,
    exhaustion: 'forbearance',
    effects: [{ type: 'aura', aura: 'blessing-of-protection' }],
  },
  'hallowed-ground': {
    name: 'Hallowed Ground',
    description:
      'Consecrates an area for 8 sec. Every second, enemies inside take holy damage and allies inside are healed.',
    school: 'holy',
    target: 'ground',
    cost: 400,
    cooldown: 30,
    range: 30,
    effects: [
      { type: 'zone', zone: 'hallowed-ground' },
      { type: 'zone', zone: 'hallowed-ground-heal' },
    ],
  },
  rejuvenation: {
    name: 'Rejuvenation',
    description: 'Heals the target over 12 sec.',
    school: 'nature',
    target: 'friendly',
    cost: 350,
    range: 40,
    effects: [{ type: 'aura', aura: 'rejuvenation' }],
  },
  regrowth: {
    name: 'Regrowth',
    description: 'Heals the target, then heals it again over 6 sec.',
    school: 'nature',
    target: 'friendly',
    cost: 500,
    castTime: 2,
    range: 40,
    effects: [
      { type: 'heal', amount: 500 },
      { type: 'aura', aura: 'regrowth' },
    ],
  },
  moonfire: {
    name: 'Moonfire',
    description: 'Instant arcane damage, then more over 12 sec.',
    school: 'arcane',
    target: 'enemy',
    cost: 250,
    range: 30,
    effects: [
      { type: 'damage', amount: 200 },
      { type: 'aura', aura: 'moonfire' },
    ],
  },
  wrath: {
    name: 'Wrath',
    description: 'Hurls a bolt of nature energy at the enemy.',
    school: 'nature',
    target: 'enemy',
    cost: 350,
    castTime: 2,
    range: 30,
    projectileSpeed: 24,
    effects: [{ type: 'damage', amount: 450 }],
  },
  'entangling-roots': {
    name: 'Entangling Roots',
    description:
      'Roots the enemy in place for 8 sec. Enough damage breaks the roots.',
    school: 'nature',
    target: 'enemy',
    cost: 250,
    castTime: 1.5,
    range: 30,
    effects: [{ type: 'aura', aura: 'entangling-roots' }],
  },
  cyclone: {
    name: 'Cyclone',
    description:
      'Tosses the enemy into the air for 6 sec. It cannot act, and cannot be damaged or healed.',
    school: 'nature',
    target: 'enemy',
    cost: 300,
    castTime: 1.5,
    range: 20,
    effects: [{ type: 'aura', aura: 'cyclone' }],
  },
  barkskin: {
    name: 'Barkskin',
    description:
      'Reduces damage taken by 20% for 12 sec. Usable while stunned.',
    school: 'physical',
    target: 'self',
    cooldown: 60,
    gcd: 0,
    usableWhileCc: true,
    effects: [{ type: 'aura', aura: 'barkskin', self: true }],
  },
  bloom: {
    name: 'Bloom',
    description:
      'Heals allies in an area, then leaves blossoms that heal anyone standing in them every second for 6 sec.',
    school: 'nature',
    target: 'ground',
    cost: 500,
    cooldown: 15,
    range: 30,
    effects: [
      {
        type: 'aoe',
        radius: 5,
        friendly: true,
        effects: [{ type: 'heal', amount: 300 }],
      },
      { type: 'zone', zone: 'bloom' },
    ],
  },
  'bear-form': {
    name: 'Bear Form',
    description:
      'Shapeshift into a bear, shedding roots and snares and taking 25% less damage. Any other spell shifts you back.',
    school: 'nature',
    target: 'self',
    cost: 300,
    gcd: 1,
    cancelable: true,
    effects: [
      { type: 'breakCc', cc: ['root'] },
      { type: 'removeSnares' },
      { type: 'aura', aura: 'bear-form', self: true },
    ],
  },
  'travel-form': {
    name: 'Travel Form',
    description:
      'Shapeshift into a stag, shedding roots and snares and moving 40% faster. Any other spell shifts you back.',
    school: 'nature',
    target: 'self',
    cost: 300,
    gcd: 1,
    cancelable: true,
    effects: [
      { type: 'breakCc', cc: ['root'] },
      { type: 'removeSnares' },
      { type: 'aura', aura: 'travel-form', self: true },
    ],
  },
  'mighty-bash': {
    name: 'Mighty Bash',
    description: 'Bashes the target, stunning it for 4 sec.',
    school: 'physical',
    target: 'enemy',
    cost: 200,
    cooldown: 50,
    effects: [{ type: 'aura', aura: 'mighty-bash' }],
  },
  thornfield: {
    name: 'Thornfield',
    description:
      'Raises brambles over an area for 8 sec. Enemies inside take nature damage every second and are slowed by 30%.',
    school: 'nature',
    target: 'ground',
    cost: 350,
    cooldown: 25,
    range: 30,
    effects: [{ type: 'zone', zone: 'thornfield' }],
  },
  'lightning-bolt': {
    name: 'Lightning Bolt',
    description: 'Strikes the enemy with a bolt of lightning.',
    school: 'nature',
    target: 'enemy',
    cost: 350,
    castTime: 2,
    range: 30,
    effects: [{ type: 'damage', amount: 460 }],
  },
  'chain-lightning': {
    name: 'Chain Lightning',
    description:
      'Lightning that strikes the enemy, then leaps to the nearest enemy, and on to one more. Each leap deals 30% less damage.',
    school: 'nature',
    target: 'enemy',
    cost: 500,
    castTime: 2,
    cooldown: 8,
    range: 30,
    effects: [
      {
        type: 'chain',
        jumps: 2,
        radius: 10,
        falloff: 0.7,
        effects: [{ type: 'damage', amount: 380 }],
      },
    ],
  },
  'flame-shock': {
    name: 'Flame Shock',
    description: 'Instant fire damage, then more over 12 sec.',
    school: 'fire',
    target: 'enemy',
    cost: 250,
    cooldown: 6,
    range: 25,
    effects: [
      { type: 'damage', amount: 180 },
      { type: 'aura', aura: 'flame-shock' },
    ],
  },
  'frost-shock': {
    name: 'Frost Shock',
    description: 'Instant frost damage that slows the enemy by 50% for 8 sec.',
    school: 'frost',
    target: 'enemy',
    cost: 250,
    cooldown: 10,
    range: 25,
    effects: [
      { type: 'damage', amount: 200 },
      { type: 'aura', aura: 'frost-shock' },
    ],
  },
  'wind-shear': {
    name: 'Wind Shear',
    description: 'Interrupts spellcasting, locking out that school for 3 sec.',
    school: 'nature',
    target: 'enemy',
    cost: 100,
    cooldown: 15,
    gcd: 0,
    range: 25,
    effects: [{ type: 'interrupt', lockout: 3 }],
  },
  purge: {
    name: 'Purge',
    description: 'Removes one helpful magic effect from the enemy.',
    school: 'nature',
    target: 'enemy',
    cost: 250,
    cooldown: 8,
    range: 30,
    effects: [{ type: 'dispel', count: 1 }],
  },
  hex: {
    name: 'Hex',
    description:
      'Turns the enemy into a frog, incapacitating it for 6 sec. Enough damage breaks it.',
    school: 'nature',
    target: 'enemy',
    cost: 300,
    castTime: 1.5,
    cooldown: 20,
    range: 25,
    effects: [{ type: 'aura', aura: 'hex' }],
  },
  'healing-wave': {
    name: 'Healing Wave',
    description: 'A slow, powerful heal.',
    school: 'nature',
    target: 'friendly',
    cost: 650,
    castTime: 2.5,
    range: 40,
    effects: [{ type: 'heal', amount: 1050 }],
  },
  'chain-heal': {
    name: 'Chain Heal',
    description:
      'Heals the target, then leaps to the most injured ally nearby, and on to one more. Each leap heals 40% less.',
    school: 'nature',
    target: 'friendly',
    cost: 550,
    castTime: 2,
    range: 40,
    effects: [
      {
        type: 'chain',
        jumps: 2,
        radius: 12,
        falloff: 0.6,
        effects: [{ type: 'heal', amount: 550 }],
      },
    ],
  },
  riptide: {
    name: 'Riptide',
    description: 'Instantly heals the target, then heals it again over 12 sec.',
    school: 'nature',
    target: 'friendly',
    cost: 350,
    cooldown: 6,
    range: 40,
    effects: [
      { type: 'heal', amount: 300 },
      { type: 'aura', aura: 'riptide' },
    ],
  },
  'healing-stream-totem': {
    name: 'Healing Stream Totem',
    description:
      'Plants a totem at your feet that heals allies within 8 yards every 2 sec for 15 sec.',
    school: 'nature',
    target: 'self',
    cost: 300,
    cooldown: 30,
    effects: [{ type: 'zone', zone: 'healing-stream-totem' }],
  },
  'earthbind-totem': {
    name: 'Earthbind Totem',
    description:
      'Plants a totem at your feet that slows enemies within 8 yards by 50% for 15 sec.',
    school: 'nature',
    target: 'self',
    cost: 200,
    cooldown: 30,
    effects: [{ type: 'zone', zone: 'earthbind-totem' }],
  },
  'tremor-totem': {
    name: 'Tremor Totem',
    description:
      'Plants a totem at your feet that shakes allies within 10 yards out of fear every second for 20 sec.',
    school: 'nature',
    target: 'self',
    cost: 200,
    cooldown: 40,
    effects: [{ type: 'zone', zone: 'tremor-totem' }],
  },
  'grounding-totem': {
    name: 'Grounding Totem',
    description:
      'Plants a totem at your feet for 10 sec. The next enemy spell that lands on an ally within 8 yards strikes the totem instead.',
    school: 'nature',
    target: 'self',
    cost: 200,
    cooldown: 25,
    gcd: 0,
    effects: [{ type: 'zone', zone: 'grounding-totem' }],
  },
} satisfies Record<string, AbilityDef>;

export type AbilityId = keyof typeof ABILITIES;
export const ABILITY_IDS = Object.keys(ABILITIES) as AbilityId[];

export const CLASSES: Record<ClassId, ClassDef> = {
  warrior: {
    name: 'Warrior',
    color: '#c79c6e',
    description:
      'Relentless melee. Mortal Strike cripples heals; Charge closes in.',
    innate: 'plate-armor',
    talents: {
      arrival: ['battle-cry', 'intercept', 'bloodlust'],
      departure: ['piercing-roar', 'defensive-stance', 'parting-blow'],
      discipline: ['juggernaut', 'second-wind', 'berserker'],
    },
    resource: 'rage',
    maxResource: 100,
    startResource: 0,
    regen: 0,
    gcd: GCD,
    swing: { interval: 2.6, damage: 220, rage: 12 },
    abilities: [
      'mortal-strike',
      'hamstring',
      'rend',
      'execute',
      'charge',
      'pummel',
      'intimidating-shout',
      'berserker-rage',
      'shield-wall',
      'spell-reflection',
      'piercing-howl',
      'war-banner',
    ],
  },
  mage: {
    name: 'Mage',
    color: '#3fc7eb',
    description:
      'Ranged control. Kites with Frostbolt, locks down with Polymorph.',
    innate: 'warding',
    talents: {
      arrival: ['presence-of-mind', 'frost-armor', 'shimmer'],
      departure: ['parting-frost', 'blink-out', 'displacement'],
      discipline: ['glass-cannon', 'shatter', 'ice-ward'],
    },
    resource: 'mana',
    maxResource: 10000,
    startResource: 10000,
    regen: 110,
    gcd: GCD,
    swing: { interval: 2.8, damage: 90 },
    caster: true,
    abilities: [
      'frostbolt',
      'fireball',
      'fire-blast',
      'polymorph',
      'frost-nova',
      'counterspell',
      'blink',
      'ice-barrier',
      'ice-block',
      'hailstorm',
      'ice-lance',
      'rune-of-detonation',
    ],
  },
  priest: {
    name: 'Priest',
    color: '#f0f0f0',
    description: 'Healer with teeth. Shields, heals, dispels and fears.',
    innate: 'warding',
    talents: {
      arrival: ['renewal', 'inner-focus', 'power-infusion'],
      departure: ['parting-shield', 'psychic-shriek', 'fade'],
      discipline: ['atonement', 'martyrdom', 'borrowed-time'],
    },
    resource: 'mana',
    maxResource: 10000,
    startResource: 10000,
    regen: 100,
    gcd: GCD,
    swing: { interval: 2.8, damage: 90 },
    caster: true,
    abilities: [
      'flash-heal',
      'renew',
      'power-word-shield',
      'pain-suppression',
      'dispel-magic',
      'shadow-word-pain',
      'mind-blast',
      'mind-flay',
      'psychic-scream',
      'silence',
      'mass-dispel',
      'spirit-lantern',
    ],
  },
  rogue: {
    name: 'Rogue',
    color: '#fff468',
    description: 'Stealthy burst. Opens with Cheap Shot and chains stuns.',
    innate: 'leather-armor',
    talents: {
      arrival: ['shadowstep', 'adrenaline', 'ambush'],
      departure: ['smoke-bomb', 'feint', 'poisoned-exit'],
      discipline: ['fleet-footed', 'master-of-subtlety', 'ruthlessness'],
    },
    resource: 'energy',
    maxResource: 100,
    startResource: 100,
    regen: 10,
    gcd: 1,
    swing: { interval: 1.8, damage: 150 },
    abilities: [
      'sinister-strike',
      'eviscerate',
      'kidney-shot',
      'cheap-shot',
      'kick',
      'stealth',
      'gouge',
      'vanish',
      'sprint',
      'blind',
      'cloak-of-shadows',
      'caltrops',
    ],
  },
  hunter: {
    name: 'Hunter',
    color: '#aad372',
    description:
      'Ranged pressure. Shots and stings from afar; Scatter to peel.',
    innate: 'mail-armor',
    talents: {
      arrival: ['lock-and-load', 'aspect-of-the-cheetah', 'rapid-fire'],
      departure: ['parting-leap', 'parting-trap', 'flash-flare'],
      discipline: ['steady-focus', 'survivalist', 'hawk-eye'],
    },
    resource: 'mana',
    maxResource: 10000,
    startResource: 10000,
    regen: 100,
    gcd: GCD,
    swing: { interval: 2.5, damage: 200, range: 35, minRange: 5, aim: 0.5 },
    abilities: [
      'steady-shot',
      'arcane-shot',
      'aimed-shot',
      'serpent-sting',
      'concussive-shot',
      'scatter-shot',
      'silencing-shot',
      'tranquilizing-shot',
      'disengage',
      'jaw-trap',
      'freezing-trap',
      'flare',
    ],
  },
  paladin: {
    name: 'Paladin',
    color: '#f48cba',
    description:
      'Plate-clad hybrid. Heals, cleanses, stuns and bubbles to safety.',
    innate: 'plate-armor',
    talents: {
      arrival: ['divine-purpose', 'hand-of-freedom', 'avenging-wrath'],
      departure: ['purify', 'lay-on-hands', 'consecrated-exit'],
      discipline: ['sanctified', 'illumination', 'seal-of-light'],
    },
    resource: 'mana',
    maxResource: 10000,
    startResource: 10000,
    regen: 100,
    gcd: GCD,
    swing: { interval: 3, damage: 230 },
    abilities: [
      'crusader-strike',
      'judgement',
      'hammer-of-wrath',
      'hammer-of-justice',
      'flash-of-light',
      'holy-light',
      'cleanse',
      'blessing-of-freedom',
      'divine-shield',
      'repentance',
      'blessing-of-protection',
      'hallowed-ground',
    ],
  },
  druid: {
    name: 'Druid',
    color: '#ff7c0a',
    description:
      'Slippery healer. Cyclones, Roots, and shapeshifts out of snares.',
    innate: 'leather-armor',
    talents: {
      arrival: ['rejuvenating-arrival', 'natures-swiftness', 'feral-instinct'],
      departure: ['bramble-exit', 'bark-shell', 'shed-skin'],
      discipline: ['thick-hide', 'natures-grace', 'shapeshifter'],
    },
    resource: 'mana',
    maxResource: 10000,
    startResource: 10000,
    regen: 100,
    gcd: GCD,
    swing: { interval: 3, damage: 120 },
    caster: true,
    abilities: [
      'rejuvenation',
      'regrowth',
      'moonfire',
      'wrath',
      'entangling-roots',
      'cyclone',
      'barkskin',
      'bear-form',
      'travel-form',
      'bloom',
      'mighty-bash',
      'thornfield',
    ],
  },
  shaman: {
    name: 'Shaman',
    color: '#0070dd',
    description:
      'Totem-bound healer. Chain spells leap; totems hold the ground.',
    innate: 'mail-armor',
    talents: {
      arrival: ['tidal-arrival', 'elemental-mastery', 'totemic-recall'],
      departure: ['grounded-exit', 'earthbind-exit', 'thunderstorm'],
      discipline: ['earth-shield', 'totemic-endurance', 'ancestral-spirit'],
    },
    resource: 'mana',
    maxResource: 10000,
    startResource: 10000,
    regen: 100,
    gcd: GCD,
    swing: { interval: 3, damage: 140 },
    caster: true,
    abilities: [
      'lightning-bolt',
      'chain-lightning',
      'flame-shock',
      'frost-shock',
      'wind-shear',
      'healing-wave',
      'chain-heal',
      'riptide',
      'grounding-totem',
      'earthbind-totem',
      'purge',
      'tremor-totem',
      'healing-stream-totem',
      'hex',
    ],
  },
};

export const abilityDef = (id: AbilityId): AbilityDef => ABILITIES[id];
export const auraDef = (id: AuraId): AuraDef => AURAS[id];
export const zoneDef = (id: ZoneId): ZoneDef => ZONES[id];
/** The bearer may click it off. */
export const cancelableAura = (def: AuraDef): boolean =>
  !def.harmful && !def.exhaustion && !def.passive;

export const CLASS_IDS = Object.keys(CLASSES) as ClassId[];
export const isClassId = (id: string): id is ClassId => id in CLASSES;
export const isAbilityId = (id: string): id is AbilityId => id in ABILITIES;

/** Every player fights as two different classes, starting each match as the first. */
export type ClassPair = [ClassId, ClassId];
export const isPair = (ids: string[]): ids is ClassPair =>
  ids.length === 2 && ids[0] !== ids[1] && ids.every(isClassId);
/** The class an ability belongs to; null for utility spells and Swap Class. */
export const abilityClass = (id: AbilityId): ClassId | null =>
  CLASS_IDS.find((c) => CLASSES[c].abilities.includes(id)) ?? null;

export type TalentSlot = 'arrival' | 'departure' | 'discipline';
export const TALENT_SLOTS: TalentSlot[] = [
  'arrival',
  'departure',
  'discipline',
];
/** A class's pick in each slot, in `TALENT_SLOTS` order. */
export type Talents = [AbilityId, AbilityId, AuraId];
export const defaultTalents = (classId: ClassId): Talents => {
  const { arrival, departure, discipline } = CLASSES[classId].talents;
  return [arrival[0], departure[0], discipline[0]];
};
/** One of the class's options in each slot. */
export function isTalents(classId: ClassId, ids: string[]): ids is Talents {
  const talents = CLASSES[classId].talents;
  return (
    ids.length === TALENT_SLOTS.length &&
    TALENT_SLOTS.every((slot, i) =>
      (talents[slot] as string[]).includes(ids[i]),
    )
  );
}
export const isTalent = (id: string): boolean =>
  CLASS_IDS.some((c) =>
    TALENT_SLOTS.some((slot) =>
      (CLASSES[c].talents[slot] as string[]).includes(id),
    ),
  );
/** The class a discipline belongs to; `own` numbers count only for its abilities. */
export const disciplineClass = (id: AuraId): ClassId | null =>
  CLASS_IDS.find((c) => CLASSES[c].talents.discipline.includes(id)) ?? null;
const counts = (id: AuraId, abilityId: AbilityId): boolean =>
  !auraDef(id).own || disciplineClass(id) === abilityClass(abilityId);
/** What an ability costs a unit carrying these auras. */
export function costOf(abilityId: AbilityId, auras: AuraId[]): number {
  const cost = abilityDef(abilityId).cost ?? 0;
  const thrift = auras
    .filter((id) => counts(id, abilityId))
    .reduce((sum, id) => sum + (auraDef(id).thrift ?? 0), 0);
  return Math.round(cost * (1 - thrift));
}
/** How far an ability reaches for a unit carrying these auras: edge to edge, melee if it names no range. */
export function rangeOf(abilityId: AbilityId, auras: AuraId[]): number {
  const ability = abilityDef(abilityId);
  const base = ability.range ?? (ability.target === 'enemy' ? MELEE_RANGE : 0);
  if (!base) return 0;
  return auras
    .filter((id) => counts(id, abilityId))
    .reduce((sum, id) => sum + (auraDef(id).reach ?? 0), base);
}

/** Every unit's health, whatever its classes; their innates and spells set them apart. */
export const BASE_HP = 4400;

/** Spells every class may slot into its utility slots. */
export const UTILITY: AbilityId[] = [
  'pvp-trinket',
  'bandage',
  'healing-potion',
  'regeneration-potion',
  'refreshment',
];
/** Everything the class can cast: its abilities, then the utility spells. */
export const spellbook = (classId: ClassId): AbilityId[] => [
  ...CLASSES[classId].abilities,
  ...UTILITY,
];
/** Each class brings this many of its spells (every class has at least this many); they share the utility slots. */
export const CLASS_SLOTS = 6;
export const UTILITY_SLOTS = 2;
/** Only spells on the bars a player brings into a match can be cast there. */
export const defaultBar = (classId: ClassId): AbilityId[] =>
  CLASSES[classId].abilities.slice(0, CLASS_SLOTS);
export const DEFAULT_UTILITY: AbilityId[] = ['pvp-trinket', 'bandage'];
/** A class's bar: `CLASS_SLOTS` of its own spells, no spell twice. */
export function isBar(classId: ClassId, ids: string[]): ids is AbilityId[] {
  const own: string[] = CLASSES[classId].abilities;
  return (
    ids.length === CLASS_SLOTS &&
    new Set(ids).size === ids.length &&
    ids.every((id) => own.includes(id))
  );
}
/** The utility slots: `UTILITY_SLOTS` utility spells, no spell twice. */
export function isUtility(ids: string[]): ids is AbilityId[] {
  const utility: string[] = UTILITY;
  return (
    ids.length === UTILITY_SLOTS &&
    new Set(ids).size === ids.length &&
    ids.every((id) => utility.includes(id))
  );
}

export const MAX_COMBO = 5;
/** Diminishing returns: each repeat within the window lasts this fraction as long, then immune. */
export const DR_FACTORS = [1, 0.5, 0.25];
export const DR_RESET = 18;
/** Units count as in combat this long after dealing or taking damage. */
export const COMBAT_TIMEOUT = 6;
export const PUSHBACK = 0.5;
export const MAX_PUSHBACKS = 2;
